import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ServiceReviewModal from '../components/ServiceReviewModal';
import CustomerCancellationModal from '../components/CustomerCancellationModal';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { RebookConfirmModal } from '../components/RebookConfirmModal';
import CustomerInvoicesSection from '../components/CustomerInvoicesSection';
import CustomerPendingInvoicesAlert from '../components/CustomerPendingInvoicesAlert';
import { apiService } from '../services/api';
import { Notification, Appointment, ServiceReview, GiftCard, BusinessRating, QuoteRow } from '../types';
import { hasActiveBusinessRoles } from '../utils/roleUtils';
import { getAppointmentStartIso, getTotalDuration, buildSeriesDepositLookup, isPendingSeriesDepositOutstanding, getDepositPaymentAppointmentId } from '../utils/appointmentHelpers';
import { buildRebookBookingUrl } from '../utils/rebookUtils';
import { getCustomerQuoteDisplayStatus } from '../utils/quoteDisplayStatus';
import toast from 'react-hot-toast';
import { Bell, CheckCircle, Calendar, XCircle, Star, Gift, AlertCircle, Clock, Check, ArrowLeft, Building2, User, DollarSign, MapPin, Edit, Trash2, Plus, Wallet2, ChevronRight, ChevronDown, Eye, CalendarPlus, Navigation, Mail, Settings, Grid, Globe, Store, Search, CreditCard, ArrowRight, Lock, SlidersHorizontal, Heart, Info, MessageSquare, FileText, Repeat, ListOrdered, HelpCircle, CalendarClock } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import CustomerGiftCardPurchaseModal, {
  CustomerGiftCardPurchaseContext,
  startCustomerGiftCardPurchase,
} from '../components/CustomerGiftCardPurchaseModal';
import { QRCodeSVG } from 'qrcode.react';

type CustomerSection = 'overview' | 'appointments' | 'invoices' | 'gift-cards' | 'notifications' | 'quote-requests';
type AppointmentFilter = 'all' | 'upcoming' | 'completed' | 'cancelled';

/** Resolve a customer-facing destination for a notification (Recent Activity / notifications list). */
const getCustomerNotificationHref = (notification: Notification): string => {
  const m = (notification.metadata || {}) as Record<string, unknown>;
  const appointmentId = String(m.appointmentId ?? m.appointment_id ?? '').trim();
  const quoteId = String(m.quoteId ?? m.quote_id ?? '').trim();
  const invoiceId = String(m.invoiceId ?? m.invoice_id ?? '').trim();

  switch (notification.type) {
    case 'APPOINTMENT_BOOKED':
    case 'APPOINTMENT_CANCELLED':
    case 'APPOINTMENT_RESCHEDULED':
    case 'APPOINTMENT_DEPOSIT_RECEIVED':
    case 'APPOINTMENT_DEPOSIT_REQUESTED':
      if (!appointmentId) return '/dashboard/customer/appointments';
      if (
        notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED' ||
        notification.title === 'Appointment Pending Deposit' ||
        notification.title === 'Deposit requested for appointment'
      ) {
        return `/dashboard/customer/appointments/${appointmentId}/deposit`;
      }
      return `/dashboard/customer/appointments/${appointmentId}`;
    case 'QUOTE_BUSINESS_MESSAGE':
    case 'QUOTE_OFFER_RECEIVED':
    case 'QUOTE_OFFER_ACCEPTED':
    case 'QUOTE_REQUEST_SUBMITTED':
      return quoteId
        ? `/dashboard/customer/quote-requests/${quoteId}`
        : '/dashboard/customer/quote-requests';
    case 'INVOICE_SENT':
    case 'INVOICE_PAID':
      return invoiceId
        ? `/dashboard/customer/invoices/${invoiceId}`
        : '/dashboard/customer/invoices';
    case 'GIFT_CARD_REDEEMED':
      return '/dashboard/customer/gift-cards';
    default:
      if (appointmentId) return `/dashboard/customer/appointments/${appointmentId}`;
      if (quoteId) return `/dashboard/customer/quote-requests/${quoteId}`;
      if (invoiceId) return `/dashboard/customer/invoices/${invoiceId}`;
      return '/dashboard/customer/notifications';
  }
};

const CustomerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const appointmentsRequestIdRef = useRef(0);
  const hasUserChangedAppointmentFilterRef = useRef(false);
  
  // Get section from URL or default to overview
  const getSectionFromUrl = () => {
    const path = location.pathname;
    if (path.includes('/appointments')) return 'appointments';
    if (path.includes('/quote-requests')) return 'quote-requests';
    if (path.includes('/invoices')) return 'invoices';
    if (path.includes('/gift-cards')) return 'gift-cards';
    if (path.includes('/notifications')) return 'notifications';
    return 'overview';
  };
  
  const [activeSection, setActiveSection] = useState<CustomerSection>(getSectionFromUrl());

  // Get filter from URL query parameter (for backward compatibility with existing URLs)
  const getFilterFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const filter = searchParams.get('filter');
    if (filter && ['all', 'upcoming', 'completed', 'cancelled'].includes(filter)) {
      return filter as AppointmentFilter;
    }
    // Default to 'all' initially, will be updated by useEffect after appointments are loaded
    return 'all';
  };

  const STORAGE_KEY = 'customerAppointmentsFilters';

  interface StoredFilters {
    filter: AppointmentFilter;
    businessId: string;
    professionalId: string;
    searchQuery: string;
    page: number;
    isFilterSectionOpen?: boolean;
  }

  // Load filters from localStorage
  const loadStoredFilters = (): StoredFilters => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          filter: parsed.filter && ['all', 'upcoming', 'completed', 'cancelled'].includes(parsed.filter) 
            ? parsed.filter 
            : 'all',
          businessId: parsed.businessId || '',
          professionalId: parsed.professionalId || '',
          searchQuery: parsed.searchQuery || '',
          page: parsed.page && typeof parsed.page === 'number' && parsed.page >= 1 ? parsed.page : 1,
          isFilterSectionOpen: parsed.isFilterSectionOpen || false
        };
      }
    } catch (error) {
      console.error('Error loading stored filters:', error);
    }
    return { 
      filter: 'all', 
      businessId: '', 
      professionalId: '', 
      searchQuery: '', 
      page: 1,
      isFilterSectionOpen: false
    };
  };

  // Save filters to localStorage
  const saveFilters = (filters: StoredFilters) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  const initialFilters = loadStoredFilters();

  // Appointments state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const seriesDepositLookup = React.useMemo(() => buildSeriesDepositLookup(appointments), [appointments]);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]); // Store all appointments for recently visited businesses
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentFilter, setAppointmentFilter] = useState<AppointmentFilter>(initialFilters.filter);

  // Business "message to customer" cache (keyed by business short name)
  const customerAppointmentMessageCacheRef = useRef<Record<string, { enabled: boolean; message: string }>>({});
  const [customerAppointmentMessageCache, setCustomerAppointmentMessageCache] = useState<
    Record<string, { enabled: boolean; message: string }>
  >({});
  
  // Newly booked appointments tracking (stored in sessionStorage)
  const [newlyBookedAppointmentIds, setNewlyBookedAppointmentIds] = useState<Set<string>>(new Set());
  const [appointmentPagination, setAppointmentPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  // Client-side pagination state for filtered results
  const [filteredPage, setFilteredPage] = useState(initialFilters.page);
  const itemsPerPage = 10;

  // Notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [readNotifications, setReadNotifications] = useState<Notification[]>([]);
  const [businessNames, setBusinessNames] = useState<{[businessId: string]: string}>({});

  // Service review modal state
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // Rebook modal state
  const [isRebookModalOpen, setIsRebookModalOpen] = useState(false);
  const [rebookAppointment, setRebookAppointment] = useState<Appointment | null>(null);
  
  // Cancellation modal state
  const [cancellationAppointment, setCancellationAppointment] = useState<Appointment | null>(null);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Mobile filter dropdown state
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  
  // Mobile collapsible filter section state
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(initialFilters.isFilterSectionOpen || false);
  
  // Mobile collapsible appointment cards state
  const [expandedAppointmentIds, setExpandedAppointmentIds] = useState<Set<string>>(new Set());
  
  const toggleAppointmentCard = (appointmentId: string | number) => {
    const id = String(appointmentId);
    setExpandedAppointmentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  // Manage dropdown state for next appointment
  const [isManageDropdownOpen, setIsManageDropdownOpen] = useState(false);
  const manageDropdownRef = useRef<HTMLDivElement>(null);
  
  // Manage dropdown state for appointment cards (track by appointment ID)
  const [openManageDropdowns, setOpenManageDropdowns] = useState<Set<string>>(new Set());
  const manageDropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Calendar dropdown state
  const [openCalendarDropdowns, setOpenCalendarDropdowns] = useState<Set<string>>(new Set());
  const calendarDropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Additional filter states for appointments
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>(initialFilters.businessId);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string>(initialFilters.professionalId);
  const [searchQuery, setSearchQuery] = useState<string>(initialFilters.searchQuery);
  
  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (activeSection === 'appointments') {
      saveFilters({
        filter: appointmentFilter,
        businessId: selectedBusinessId,
        professionalId: selectedProfessionalId,
        searchQuery: searchQuery,
        page: filteredPage,
        isFilterSectionOpen: isFilterSectionOpen
      });
    }
  }, [appointmentFilter, selectedBusinessId, selectedProfessionalId, searchQuery, filteredPage, isFilterSectionOpen, activeSection]);
  
  // Dropdown states for filters
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const [isProfessionalDropdownOpen, setIsProfessionalDropdownOpen] = useState(false);
  const businessDropdownRef = useRef<HTMLDivElement>(null);
  const professionalDropdownRef = useRef<HTMLDivElement>(null);
  
  // Reviews state
  const [appointmentReviews, setAppointmentReviews] = useState<{ [appointmentId: string]: ServiceReview[] }>({});
  const [reviewsLoading, setReviewsLoading] = useState(false);
  
  // Business ratings state
  const [appointmentBusinessRatings, setAppointmentBusinessRatings] = useState<{ [appointmentId: string]: BusinessRating | null }>({});

  // Gift cards state
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [giftCardsLoading, setGiftCardsLoading] = useState(false);
  const [giftCardsError, setGiftCardsError] = useState<string | null>(null);
  
  // Gift card purchase state
  const [purchaseTab, setPurchaseTab] = useState<'store' | 'universal'>('store');
  const [selectedGiftCardBusinessId, setSelectedGiftCardBusinessId] = useState<string>('');
  const [selectedGiftCardBusinessName, setSelectedGiftCardBusinessName] = useState<string>('');
  const [storeSearchQuery, setStoreSearchQuery] = useState<string>('');
  const [searchedBusinesses, setSearchedBusinesses] = useState<any[]>([]);
  const [recentBusinesses, setRecentBusinesses] = useState<any[]>([]);
  const [selectedAmount, setSelectedAmount] = useState<number>(25);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [recipientName, setRecipientName] = useState<string>('');
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [giftMessage, setGiftMessage] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [giftPurchaseStripePromise, setGiftPurchaseStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [giftPurchaseClientSecret, setGiftPurchaseClientSecret] = useState<string | null>(null);
  const [giftPurchaseContext, setGiftPurchaseContext] = useState<CustomerGiftCardPurchaseContext | null>(null);
  const [isGiftPurchaseModalOpen, setIsGiftPurchaseModalOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState<string>('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  
  // Gift card details modal state
  const [selectedGiftCardForDetails, setSelectedGiftCardForDetails] = useState<GiftCard | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Customer quote requests (My Quote Requests page)
  const [customerQuotes, setCustomerQuotes] = useState<QuoteRow[]>([]);
  const [customerQuotesLoading, setCustomerQuotesLoading] = useState(false);
  const [customerQuoteSearch, setCustomerQuoteSearch] = useState('');
  const [customerQuoteStatusFilter, setCustomerQuoteStatusFilter] = useState('');
  const [customerQuoteSortOrder, setCustomerQuoteSortOrder] = useState<'newest' | 'oldest'>('newest');

  const navigationItems = [
    { id: 'appointments', label: 'My Service Appointments', icon: '📅' },
    { id: 'quote-requests', label: 'My Quote Requests', icon: '📋' },
    // Gift Cards hidden from customer nav for now
    // { id: 'gift-cards', label: 'My Gift Cards', icon: '🎁' },
  ];

  // Add business dashboard card if user has active business roles
  // Use useMemo to recalculate when user changes
  const hasActiveBusinessRolesResult = React.useMemo(() => {
    return hasActiveBusinessRoles(user);
  }, [user]);
  
  const businessDashboardItem = React.useMemo(() => {
    return hasActiveBusinessRolesResult ? {
    id: 'business-dashboard',
    label: 'My Business Dashboard',
    icon: '🏢',
    isBusinessDashboard: true
  } : null;
  }, [hasActiveBusinessRolesResult]);

  // Load appointments function
  const loadAppointments = useCallback(async (page = 1) => {
    if (!user?.id) return;

    const requestId = ++appointmentsRequestIdRef.current;
    setAppointmentsLoading(true);
    try {
      const filters: any = {
        page,
        limit: 10
      };
      
      // Apply status filter
      if (appointmentFilter !== 'all') {
        if (appointmentFilter === 'upcoming') {
          filters.status = 'upcoming';
          filters.sort = 'asc';
        } else if (appointmentFilter === 'completed') {
          // "Past" filter: send 'past' to backend to filter by date < now (regardless of status)
          filters.status = 'past';
          filters.sort = 'desc';
        } else {
          filters.status = appointmentFilter.toUpperCase();
        }
      }
      
      const response = await apiService.getCustomerAppointments(filters);

      // Ignore stale responses (e.g., user switched filters quickly)
      if (appointmentsRequestIdRef.current !== requestId) return;
      
      setAppointments(response.data || []);

      // Fetch optional business messages (once per business short name)
      try {
        const shortNames = Array.from(
          new Set(
            (response.data || [])
              .map((a: any) => String(a?.business_short_name || '').trim())
              .filter(Boolean)
          )
        );

        const missing = shortNames.filter((sn) => !customerAppointmentMessageCacheRef.current[sn]);
        if (missing.length > 0) {
          const pairs = await Promise.all(
            missing.map(async (sn) => {
              try {
                const msg = await apiService.getCustomerAppointmentMessageByShortName(sn);
                return [sn, msg] as const;
              } catch (err) {
                console.error(`Error loading customer appointment message for ${sn}:`, err);
                return null;
              }
            })
          );

          if (appointmentsRequestIdRef.current !== requestId) return;

          const updates: Record<string, { enabled: boolean; message: string }> = {};
          for (const p of pairs) {
            if (!p) continue;
            const [sn, msg] = p;
            updates[sn] = { enabled: Boolean(msg.enabled), message: String(msg.message ?? '') };
          }

          if (Object.keys(updates).length > 0) {
            customerAppointmentMessageCacheRef.current = {
              ...customerAppointmentMessageCacheRef.current,
              ...updates,
            };
            setCustomerAppointmentMessageCache((prev) => ({ ...prev, ...updates }));
          }
        }
      } catch (e) {
        console.error('Error preparing customer appointment messages:', e);
      }
      
      // Update pagination from API response
      if (response.pagination) {
        setAppointmentPagination({
          currentPage: response.pagination.page || page,
          totalPages: response.pagination.totalPages || 1,
          total: response.pagination.total || 0,
          hasNextPage: response.pagination.hasNextPage || false,
          hasPrevPage: response.pagination.hasPrevPage || false
        });
      } else {
        // Fallback pagination if API doesn't return pagination info
        setAppointmentPagination({
          currentPage: page,
          totalPages: 1,
          total: response.data?.length || 0,
          hasNextPage: false,
          hasPrevPage: page > 1
        });
      }
      
      // Load reviews for the appointments
      setReviewsLoading(true);
      const reviewsMap: { [appointmentId: string]: ServiceReview[] } = {};
      const businessRatingsMap: { [appointmentId: string]: BusinessRating | null } = {};
      try {
        for (const appointment of response.data || []) {
          if (appointmentsRequestIdRef.current !== requestId) return;
          
          // Load service reviews
          try {
            const reviews = await apiService.getAppointmentReviews(appointment.id);
            reviewsMap[appointment.id] = reviews || [];
          } catch (reviewError) {
            console.error(`Error loading reviews for appointment ${appointment.id}:`, reviewError);
            reviewsMap[appointment.id] = [];
          }
          
          // Load business rating for the appointment
          try {
            const businessRating = await apiService.getBusinessRatingForAppointment(appointment.id);
            businessRatingsMap[appointment.id] = businessRating;
          } catch (ratingError) {
            console.error(`Error loading business rating for appointment ${appointment.id}:`, ratingError);
            businessRatingsMap[appointment.id] = null;
          }
        }
      } catch (reviewError) {
        console.error('Error loading appointment reviews:', reviewError);
      }
      if (appointmentsRequestIdRef.current !== requestId) return;
      setAppointmentReviews(reviewsMap);
      setAppointmentBusinessRatings(businessRatingsMap);
      setReviewsLoading(false);
    } catch (error) {
      console.error('Error loading appointments:', error);
      // Only surface errors for the latest request
      if (appointmentsRequestIdRef.current === requestId) {
        setAppointments([]);
        toast.error('Failed to load appointments. Please try again.');
      }
    } finally {
      if (appointmentsRequestIdRef.current === requestId) {
        setAppointmentsLoading(false);
        setReviewsLoading(false);
      }
    }
  }, [user?.id, appointmentFilter]);

  // Load gift cards function
  const loadGiftCards = useCallback(async () => {
    if (!user?.id) return;
    
    setGiftCardsLoading(true);
    setGiftCardsError(null);
    try {
      // Fetch gift cards where issued_to matches the current user ID
      const giftCardsData = await apiService.getUserGiftCards();
      
      // Filter to only include gift cards issued to this user
      const userGiftCards = giftCardsData.filter(gc => gc.issuedTo === user.id);
      
      setGiftCards(userGiftCards);
    } catch (error) {
      console.error('Error loading gift cards:', error);
      setGiftCardsError('Failed to load your gift cards. Please try again.');
      setGiftCards([]);
    } finally {
      setGiftCardsLoading(false);
    }
  }, [user?.id]);

  // Load businesses for gift card purchase
  const loadBusinessesForGiftCards = useCallback(async () => {
    try {
      const businesses = await apiService.getBusinesses();
      // Get recently visited businesses from appointments
      const recentBusinessIds = new Set(
        allAppointments
          .filter(apt => apt.businessId)
          .map(apt => apt.businessId)
          .slice(0, 5)
      );
      const recent = businesses.filter(b => recentBusinessIds.has(b.id));
      setRecentBusinesses(recent);
      
      // If searching, filter businesses
      if (storeSearchQuery.trim()) {
        const query = storeSearchQuery.toLowerCase();
        const filtered = businesses.filter(b => 
          b.name?.toLowerCase().includes(query) ||
          b.address?.toLowerCase().includes(query) ||
          b.city?.toLowerCase().includes(query)
        );
        setSearchedBusinesses(filtered.slice(0, 10));
      } else {
        setSearchedBusinesses([]);
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  }, [allAppointments, storeSearchQuery]);

  const getGiftCardPurchaseAmount = (): number => {
    if (customAmount.trim()) {
      const parsed = parseFloat(customAmount);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return selectedAmount;
  };

  // Handle gift card purchase — start Stripe checkout
  const handlePurchaseGiftCard = async () => {
    if (!user?.id) {
      toast.error('Please log in to purchase gift cards');
      return;
    }

    const amount = getGiftCardPurchaseAmount();
    if (!amount || amount < 10) {
      toast.error('Minimum amount is $10');
      return;
    }

    if (purchaseTab === 'store' && !selectedGiftCardBusinessId) {
      toast.error('Please select a store');
      return;
    }

    if (!recipientName.trim() || !recipientEmail.trim()) {
      toast.error('Please enter recipient name and email');
      return;
    }

    if (!giftPurchaseStripePromise) {
      toast.error('Payments are not available right now. Please try again later.');
      return;
    }

    setIsPurchasing(true);
    try {
      const giftCardData = {
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
        amount,
        message: giftMessage || undefined,
        expiresAt: undefined,
        deliveryDate: deliveryDate || undefined,
      };

      const context: CustomerGiftCardPurchaseContext =
        purchaseTab === 'store'
          ? {
              type: 'store',
              businessId: selectedGiftCardBusinessId,
              businessName: selectedGiftCardBusinessName || 'Store',
            }
          : { type: 'universal' };

      const { clientSecret } = await startCustomerGiftCardPurchase(context, giftCardData);
      setGiftPurchaseContext(context);
      setGiftPurchaseClientSecret(clientSecret);
      setIsGiftPurchaseModalOpen(true);
    } catch (error: any) {
      console.error('Error starting gift card purchase:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to start payment');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleGiftCardPurchaseComplete = async () => {
    setRecipientName('');
    setRecipientEmail('');
    setGiftMessage('');
    setDeliveryDate('');
    setCustomAmount('');
    setSelectedAmount(25);
    setGiftPurchaseClientSecret(null);
    setGiftPurchaseContext(null);
    await loadGiftCards();
  };

  // Handle redeem code
  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) {
      toast.error('Please enter a gift card code');
      return;
    }

    setIsRedeeming(true);
    try {
      await apiService.redeemGiftCardCode(redeemCode.trim().toUpperCase());
      toast.success('Gift card redeemed successfully!');
      setRedeemCode('');
      await loadGiftCards();
    } catch (error: any) {
      console.error('Error redeeming code:', error);
      toast.error(error.response?.data?.error || 'Failed to redeem gift card code');
    } finally {
      setIsRedeeming(false);
    }
  };

  // Load businesses when gift cards section is active
  useEffect(() => {
    if (activeSection === 'gift-cards') {
      loadBusinessesForGiftCards();
    }
  }, [activeSection, loadBusinessesForGiftCards]);

  useEffect(() => {
    if (activeSection !== 'gift-cards') return;
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setGiftPurchaseStripePromise(loadStripe(cfg.publishableKey));
      } catch (e) {
        console.error('Failed to load Stripe config for gift card purchase', e);
      }
    })();
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'gift-cards' || purchaseTab !== 'store') return;
    if (selectedGiftCardBusinessId || recentBusinesses.length === 0) return;
    const first = recentBusinesses[0];
    setSelectedGiftCardBusinessId(first.id);
    setSelectedGiftCardBusinessName(first.name || '');
  }, [activeSection, purchaseTab, recentBusinesses, selectedGiftCardBusinessId]);

  // Update business search when query changes
  useEffect(() => {
    if (activeSection === 'gift-cards' && storeSearchQuery.trim()) {
      loadBusinessesForGiftCards();
    }
  }, [storeSearchQuery, activeSection, loadBusinessesForGiftCards]);

  // Update active section when URL changes
  useEffect(() => {
    const newSection = getSectionFromUrl();
    if (newSection !== activeSection) {
      setActiveSection(newSection);
    }
  }, [location.pathname, activeSection]);

  // Update filter when URL query parameter changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'upcoming', 'completed', 'cancelled'].includes(filterParam)) {
      setAppointmentFilter(filterParam as AppointmentFilter);
    }
    
    // Check for businessId query parameter and pre-select the business
    const businessIdParam = searchParams.get('businessId');
    if (businessIdParam && activeSection === 'appointments') {
      setSelectedBusinessId(businessIdParam);
    }
    
    // Check for newlyBooked query parameter
    const newlyBookedId = searchParams.get('newlyBooked');
    if (newlyBookedId && activeSection === 'appointments') {
      // Store in sessionStorage with timestamp (expires after 5 minutes)
      const timestamp = Date.now();
      const expiryTime = timestamp + (5 * 60 * 1000); // 5 minutes
      const storedData = {
        appointmentId: newlyBookedId,
        timestamp,
        expiryTime
      };
      sessionStorage.setItem(`newlyBooked_${newlyBookedId}`, JSON.stringify(storedData));
      
      // Update state
      setNewlyBookedAppointmentIds(prev => new Set([...Array.from(prev), newlyBookedId]));
      
      // Remove from URL to keep it clean
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('newlyBooked');
      const newUrl = `${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, activeSection]);
  
  // Load newly booked appointments from sessionStorage on mount
  useEffect(() => {
    const now = Date.now();
    const newlyBookedSet = new Set<string>();
    
    // Check all sessionStorage keys for newly booked appointments
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('newlyBooked_')) {
        try {
          const storedData = JSON.parse(sessionStorage.getItem(key) || '{}');
          if (storedData.expiryTime && now < storedData.expiryTime) {
            newlyBookedSet.add(storedData.appointmentId);
          } else {
            // Expired, remove it
            sessionStorage.removeItem(key);
          }
        } catch (e) {
          // Invalid data, remove it
          sessionStorage.removeItem(key);
        }
      }
    }
    
    setNewlyBookedAppointmentIds(newlyBookedSet);
  }, []);
  
  // Clean up expired newly booked indicators periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const validIds = new Set<string>();
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('newlyBooked_')) {
          try {
            const storedData = JSON.parse(sessionStorage.getItem(key) || '{}');
            if (storedData.expiryTime && now < storedData.expiryTime) {
              validIds.add(storedData.appointmentId);
            } else {
              sessionStorage.removeItem(key);
            }
          } catch (e) {
            sessionStorage.removeItem(key);
          }
        }
      }
      
      setNewlyBookedAppointmentIds(validIds);
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  // Set default filter based on appointments after they're loaded (only if no filter in URL)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    // Only set default if no filter in URL and we have appointments loaded
    if (!filterParam && allAppointments.length > 0 && !hasUserChangedAppointmentFilterRef.current) {
      const now = new Date();
      const hasPendingOrConfirmed = allAppointments.some(apt => {
        const iso = getAppointmentStartIso(apt);
        if (!iso) return false;
        const aptDate = new Date(iso);
        return aptDate > now && (apt.status === 'CONFIRMED' || apt.status === 'PENDING');
      });
      const defaultFilter = hasPendingOrConfirmed ? 'upcoming' : 'completed';
      // Only auto-set once, and never override an explicit user choice (including "All")
      if (appointmentFilter === 'all') {
        setAppointmentFilter(defaultFilter);
      }
    }
  }, [allAppointments, location.search, appointmentFilter]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  // Close manage dropdown when clicking outside
  useEffect(() => {
    if (!isManageDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (manageDropdownRef.current && !manageDropdownRef.current.contains(event.target as Node)) {
        setIsManageDropdownOpen(false);
      }
    };

    // Use setTimeout to ensure this runs after the button click handler
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isManageDropdownOpen]);
  
  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(event.target as Node)) {
        setIsBusinessDropdownOpen(false);
      }
    };

    if (isBusinessDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBusinessDropdownOpen]);
  
  // Close professional dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (professionalDropdownRef.current && !professionalDropdownRef.current.contains(event.target as Node)) {
        setIsProfessionalDropdownOpen(false);
      }
    };

    if (isProfessionalDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfessionalDropdownOpen]);
  
  // Close calendar dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(calendarDropdownRefs.current).forEach(appointmentId => {
        const ref = calendarDropdownRefs.current[appointmentId];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenCalendarDropdowns(prev => {
            const newSet = new Set(prev);
            newSet.delete(appointmentId);
            return newSet;
          });
        }
      });
    };

    if (openCalendarDropdowns.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openCalendarDropdowns]);

  // Close manage dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(manageDropdownRefs.current).forEach(appointmentId => {
        const ref = manageDropdownRefs.current[appointmentId];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenManageDropdowns(prev => {
            const newSet = new Set(prev);
            newSet.delete(appointmentId);
            return newSet;
          });
        }
      });
    };

    if (openManageDropdowns.size > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openManageDropdowns]);

  // Force re-evaluation of business roles when user changes
  useEffect(() => {
    // This effect ensures the component re-renders when user object changes
    // The useMemo hooks above will automatically recalculate
  }, [user]);

  // Load all appointments for recently visited businesses
  const loadAllAppointments = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Fetch all appointments for the customer (with a high limit to get all)
      const response = await apiService.getCustomerAppointments({ limit: 1000 });
      setAllAppointments(response.data || []);
    } catch (error) {
      console.error('Error loading all appointments:', error);
      // On error, set empty array so the card doesn't show
      setAllAppointments([]);
    }
  }, [user?.id]);

  // Format appointment subtext based on date/time conditions
  const formatAppointmentSubtext = useCallback((appointmentDate: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const appointmentDay = new Date(appointmentDate.getFullYear(), appointmentDate.getMonth(), appointmentDate.getDate());
    
    const isToday = appointmentDay.getTime() === today.getTime();
    const isFuture = appointmentDate > now;
    
    const timeStr = appointmentDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    if (isFuture && !isToday) {
      // Condition 1: Future date
      const monthDay = appointmentDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `Upcoming: ${monthDay} at ${timeStr}`;
    } else if (isToday && isFuture) {
      // Condition 2: Today, time in the future
      return `Upcoming: Today at ${timeStr}`;
    } else if (isToday && !isFuture) {
      // Condition 3: Today, time has passed
      return `Earlier Today at ${timeStr}`;
    } else {
      // Condition 4: Past date
      const monthDay = appointmentDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `Last: ${monthDay} at ${timeStr}`;
    }
  }, []);

  // Get recently visited businesses
  const getRecentlyVisitedBusinesses = useCallback(() => {
    if (allAppointments.length === 0) return [];

    const now = new Date();
    // Create a map to track unique businesses with their most recent past appointment, or next future if no past exists
    const businessMap = new Map<string, { businessId: string; businessName: string; businessShortName: string; lastAppointmentDate: Date; isPast: boolean }>();

    allAppointments.forEach(appointment => {
      if (!appointment.businessId || !appointment.business_name) return;

      const appointmentDate = new Date(appointment.scheduledAt || appointment.createdAt);
      const isPast = appointmentDate < now;
      const existing = businessMap.get(appointment.businessId);

      if (!existing) {
        // First appointment for this business
        businessMap.set(appointment.businessId, {
          businessId: appointment.businessId,
          businessName: appointment.business_name,
          businessShortName: appointment.business_short_name || '',
          lastAppointmentDate: appointmentDate,
          isPast: isPast
        });
      } else {
        // Prefer past appointments over future ones
        if (isPast && !existing.isPast) {
          // Replace future with past
          businessMap.set(appointment.businessId, {
            ...existing,
            lastAppointmentDate: appointmentDate,
            isPast: true
          });
        } else if (isPast && existing.isPast) {
          // Both are past, keep the most recent
          if (appointmentDate > existing.lastAppointmentDate) {
            businessMap.set(appointment.businessId, {
              ...existing,
              lastAppointmentDate: appointmentDate
            });
          }
        } else if (!isPast && !existing.isPast) {
          // Both are future, keep the earliest (next upcoming)
          if (appointmentDate < existing.lastAppointmentDate) {
            businessMap.set(appointment.businessId, {
              ...existing,
              lastAppointmentDate: appointmentDate
            });
          }
        }
        // If existing is past and current is future, keep the past one (do nothing)
      }
    });

    // Convert to array, sort by most recent appointment date, and limit to 10
    return Array.from(businessMap.values())
      .sort((a, b) => b.lastAppointmentDate.getTime() - a.lastAppointmentDate.getTime())
      .slice(0, 10);
  }, [allAppointments]);

  // Ensure reviews/ratings are loaded for all displayed appointments
  useEffect(() => {
    if (activeSection !== 'appointments' || !user?.id || appointments.length === 0) return;
    
    const loadMissingReviewsAndRatings = async () => {
      const missingReviews: string[] = [];
      const missingRatings: string[] = [];
      
      // Check which appointments are missing reviews or ratings
      for (const appointment of appointments) {
        if (appointmentReviews[appointment.id] === undefined) {
          missingReviews.push(appointment.id);
        }
        if (appointmentBusinessRatings[appointment.id] === undefined) {
          missingRatings.push(appointment.id);
        }
      }
      
      // Load missing reviews
      if (missingReviews.length > 0) {
        const newReviewsMap = { ...appointmentReviews };
        for (const appointmentId of missingReviews) {
          try {
            const reviews = await apiService.getAppointmentReviews(appointmentId);
            newReviewsMap[appointmentId] = reviews || [];
          } catch (error) {
            console.error(`Failed to load reviews for ${appointmentId}:`, error);
            newReviewsMap[appointmentId] = [];
          }
        }
        setAppointmentReviews(newReviewsMap);
      }
      
      // Load missing ratings
      if (missingRatings.length > 0) {
        const newRatingsMap = { ...appointmentBusinessRatings };
        for (const appointmentId of missingRatings) {
          try {
            const rating = await apiService.getBusinessRatingForAppointment(appointmentId);
            newRatingsMap[appointmentId] = rating;
          } catch (error) {
            console.error(`Failed to load rating for ${appointmentId}:`, error);
            newRatingsMap[appointmentId] = null;
          }
        }
        setAppointmentBusinessRatings(newRatingsMap);
      }
    };
    
    loadMissingReviewsAndRatings();
  }, [appointments, activeSection, user?.id]);

  // Load appointments when appointments section is active or overview
  useEffect(() => {
    if (activeSection === 'appointments' || activeSection === 'overview') {
      loadAppointments(1);
    }
  }, [activeSection, loadAppointments]);

  // Load all appointments on mount for recently visited businesses
  useEffect(() => {
    loadAllAppointments();
  }, [loadAllAppointments]);

  // Load gift cards when gift-cards section is active
  useEffect(() => {
    if (activeSection === 'gift-cards') {
      loadGiftCards();
    }
  }, [activeSection, loadGiftCards]);

  // Get filtered appointments (client-side filtering for business, professional, and search)
  const getFilteredAppointments = () => {
    let filtered = appointments;
    
    // Filter by business
    if (selectedBusinessId) {
      filtered = filtered.filter(apt => apt.businessId === selectedBusinessId);
    }
    
    // Filter by professional
    if (selectedProfessionalId) {
      filtered = filtered.filter(apt => 
        apt.assignments?.some(assignment => assignment.professionalId === selectedProfessionalId)
      );
    }
    
    // Filter by search query (service name or notes)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(apt => {
        // Check service names
        const matchesServiceName = apt.assignments?.some(assignment => 
          assignment.serviceName?.toLowerCase().includes(query)
        );
        
        // Check appointment notes
        const matchesNotes = apt.notes?.toLowerCase().includes(query);
        
        // Check assignment notes
        const matchesAssignmentNotes = apt.assignments?.some(assignment => 
          assignment.notes?.toLowerCase().includes(query)
        );
        
        return matchesServiceName || matchesNotes || matchesAssignmentNotes;
      });
    }
    
    // Sort by scheduled date (upcoming: soonest first; others: newly booked first, then date asc)
    filtered = filtered.sort((a, b) => {
      const aIso = getAppointmentStartIso(a);
      const bIso = getAppointmentStartIso(b);
      const aDate = aIso ? new Date(aIso).getTime() : 0;
      const bDate = bIso ? new Date(bIso).getTime() : 0;

      if (appointmentFilter !== 'upcoming') {
        const aIsNewlyBooked = newlyBookedAppointmentIds.has(String(a.id));
        const bIsNewlyBooked = newlyBookedAppointmentIds.has(String(b.id));
        if (aIsNewlyBooked && !bIsNewlyBooked) return -1;
        if (!aIsNewlyBooked && bIsNewlyBooked) return 1;
      }

      return aDate - bDate;
    });
    
    return filtered;
  };
  
  // Get unique businesses from all appointments (fallback to current appointments if allAppointments is empty)
  const getUniqueBusinesses = () => {
    const appointmentsToUse = allAppointments.length > 0 ? allAppointments : appointments;
    const businessMap = new Map<string, { id: string; name: string }>();
    appointmentsToUse.forEach(apt => {
      if (apt.businessId && apt.business_name) {
        if (!businessMap.has(apt.businessId)) {
          businessMap.set(apt.businessId, {
            id: apt.businessId,
            name: apt.business_name
          });
        }
      }
    });
    return Array.from(businessMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Get unique professionals from all appointments (fallback to current appointments if allAppointments is empty)
  const getUniqueProfessionals = () => {
    const appointmentsToUse = allAppointments.length > 0 ? allAppointments : appointments;
    const professionalMap = new Map<string, { id: string; name: string }>();
    appointmentsToUse.forEach(apt => {
      apt.assignments?.forEach(assignment => {
        if (assignment.professionalId && assignment.professionalFirstName && assignment.professionalLastName) {
          const fullName = `${assignment.professionalFirstName} ${assignment.professionalLastName}`;
          if (!professionalMap.has(assignment.professionalId)) {
            professionalMap.set(assignment.professionalId, {
              id: assignment.professionalId,
              name: fullName
            });
          }
        }
      });
    });
    return Array.from(professionalMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Get appointment counts for overview
  const getAppointmentCounts = () => {
    // Count upcoming appointments (CONFIRMED or PENDING with scheduledAt after current date)
    const now = new Date();
    const upcomingCount = allAppointments.filter(apt => {
      const iso = getAppointmentStartIso(apt);
      if (!iso) return false;
      const aptDate = new Date(iso);
      // Use local date comparison - appointment must be in the future
      // and status must be CONFIRMED or PENDING
      return aptDate > now && (apt.status === 'CONFIRMED' || apt.status === 'PENDING');
    }).length;

    return {
      upcoming: upcomingCount,
      completed: 0, // TODO: Implement separate API call for overview counts
      cancelled: 0, // TODO: Implement separate API call for overview counts
      total: allAppointments.length
    };
  };

  // Get next upcoming appointment
  const getNextAppointment = () => {
    if (allAppointments.length === 0) return null;
    const now = new Date();
    const upcoming = allAppointments
      .filter(apt => {
        const iso = getAppointmentStartIso(apt);
        if (!iso) return false;
        const aptDate = new Date(iso);
        // Use local date comparison - appointment must be after current date
        // and status must be CONFIRMED or PENDING
        return aptDate > now && (apt.status === 'CONFIRMED' || apt.status === 'PENDING');
      })
      .sort((a, b) => {
        const aIso = getAppointmentStartIso(a);
        const bIso = getAppointmentStartIso(b);
        return (aIso ? new Date(aIso).getTime() : 0) - (bIso ? new Date(bIso).getTime() : 0);
      });
    return upcoming.length > 0 ? upcoming[0] : null;
  };

  // Load notifications function
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setNotificationsLoading(true);
    try {
      // Fetch customer notifications from API
      // Calling without businessId to get all notifications for the customer
      // The backend endpoint /notifications/business filters by receiver_id (user ID)
      // so it works for both customers and business users
      const notificationsResponse = await apiService.getNotifications();
      const notificationsData = notificationsResponse.data || [];
      
      
      setNotifications(notificationsData);
      
      // Separate unread and read notifications
      const unread = notificationsData.filter((notification: Notification) => !notification.is_read);
      const read = notificationsData.filter((notification: Notification) => notification.is_read);
      setUnreadNotifications(unread);
      setReadNotifications(read);

      // Extract unique business IDs and fetch business names
      const uniqueBusinessIds = Array.from(
        new Set(
          notificationsData
            .filter((n: Notification) => n.business_id && n.business_id !== 'system')
            .map((n: Notification) => n.business_id)
        )
      );

      if (uniqueBusinessIds.length > 0) {
        // Fetch all businesses and create a lookup map
        try {
          const allBusinesses = await apiService.getBusinesses();
          const businessNameMap: {[businessId: string]: string} = {};
          
          uniqueBusinessIds.forEach(businessId => {
            const business = allBusinesses.find((b: any) => b.id === businessId);
            if (business) {
              businessNameMap[businessId as string] = business.name;
            }
          });
          
          setBusinessNames(businessNameMap);
        } catch (error) {
          console.error('Error fetching business names:', error);
          // Don't fail the whole operation if business names can't be fetched
        }
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications. Please try again.');
      setNotifications([]);
      setUnreadNotifications([]);
      setReadNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.id]);

  // Load notifications when notifications section is active
  useEffect(() => {
    if (activeSection === 'notifications') {
      loadNotifications();
    }
  }, [activeSection, loadNotifications]);

  // Load notifications on component mount to show badge (only once)
  useEffect(() => {
    if (user?.id && notifications.length === 0 && !notificationsLoading) {
      loadNotifications();
    }
  }, [user?.id]); // Only depend on user?.id, not on loadNotifications or notifications state

  // Load customer quote requests when quote-requests section is active
  const loadCustomerQuotes = useCallback(async () => {
    if (!user?.id) return;
    setCustomerQuotesLoading(true);
    try {
      const data = await apiService.getQuotesForCustomer(user.id, { limit: 100 });
      setCustomerQuotes(data || []);
    } catch (err) {
      console.error('Error loading quote requests:', err);
      toast.error('Failed to load quote requests');
      setCustomerQuotes([]);
    } finally {
      setCustomerQuotesLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeSection === 'quote-requests' || activeSection === 'overview') {
      loadCustomerQuotes();
    }
  }, [activeSection, loadCustomerQuotes]);

  // Handle marking notification as read
  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      
      // Update the local state
      setNotifications(prev => {
        const updatedNotifications = prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        );
        
        // Update unread and read notifications based on the updated notifications
        const unread = updatedNotifications.filter(notification => !notification.is_read);
        const read = updatedNotifications.filter(notification => notification.is_read);
        
        setUnreadNotifications(unread);
        setReadNotifications(read);
        
        return updatedNotifications;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read. Please try again.');
    }
  };

  // Handle marking all notifications as read
  const handleMarkAllNotificationsAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      
      // Update local state
      const updatedNotifications = notifications.map(notification => 
        !notification.is_read 
          ? { ...notification, is_read: true, read_at: new Date().toISOString() }
          : notification
      );
      
      setNotifications(updatedNotifications);
      setUnreadNotifications([]);
      setReadNotifications(updatedNotifications);
      
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read. Please try again.');
    }
  };

  const handleSectionChange = (section: CustomerSection) => {
    setActiveSection(section);
    
    // Navigate to the appropriate URL
    if (section === 'overview') {
      navigate('/dashboard/customer');
    } else {
      navigate(`/dashboard/customer/${section}`);
    }
  };

  const handleBusinessDashboardClick = () => {
    navigate('/dashboard/business');
  };

  const handleFilterChange = (filter: AppointmentFilter) => {
    hasUserChangedAppointmentFilterRef.current = true;
    setAppointmentFilter(filter);
    setFilteredPage(1); // Reset to first page when changing status filter
  };

  const handlePageChange = (page: number) => {
    loadAppointments(page);
  };

  const handleRateAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsReviewModalOpen(true);
  };

  const handleRebookAppointment = (appointment: Appointment) => {
    setRebookAppointment(appointment);
    setIsRebookModalOpen(true);
  };

  const handleRebookContinue = () => {
    if (!rebookAppointment) return;
    const url = buildRebookBookingUrl(rebookAppointment, 2);
    setIsRebookModalOpen(false);
    setRebookAppointment(null);
    if (url) navigate(url);
  };

  const handleRebookEditServices = () => {
    if (!rebookAppointment) return;
    const url = buildRebookBookingUrl(rebookAppointment, 1);
    setIsRebookModalOpen(false);
    setRebookAppointment(null);
    if (url) navigate(url);
  };

  const handleReviewModalClose = () => {
    setIsReviewModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleReviewSubmitted = () => {
    // Reload appointments to show updated review status
    if (activeSection === 'appointments') {
      loadAppointments(appointmentPagination.currentPage);
    }
  };

  const handleRescheduleAppointment = (appointment: Appointment) => {
    navigate(`/dashboard/customer/reschedule/${appointment.id}`);
  };

  const handleCancelAppointment = async (appointment: Appointment) => {
    setCancellationAppointment(appointment);
    setIsCancellationModalOpen(true);
  };

  const handleCancellationConfirmed = async (cancellationData: { noShow: boolean; cancellationNotes: string; cancelScope?: 'THIS' | 'REMAINING' }) => {
    if (!cancellationAppointment) return;

    setIsCancelling(true);
    try {
      await apiService.cancelAppointment(cancellationAppointment.id, cancellationData);
      toast.success('We\'re sorry to see you cancel your appointment. Please feel free to schedule a new appointment anytime - we\'d love to have you back!', {
        duration: 6000, // Show for 6 seconds to give time to read
        style: {
          background: '#764ba2',
          color: 'white',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '14px',
          lineHeight: '1.4'
        }
      });
      // Reload all data to reflect UI updates
      await Promise.all([
        loadAppointments(appointmentPagination.currentPage),
        loadAllAppointments(),
        loadNotifications()
      ]);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    } finally {
      setIsCancelling(false);
      setCancellationAppointment(null);
      setIsCancellationModalOpen(false);
    }
  };

  const handleCancellationCancelled = () => {
    setCancellationAppointment(null);
    setIsCancellationModalOpen(false);
  };
  
  // Handle viewing appointment details
  const handleViewAppointment = (appointment: Appointment) => {
    navigate(`/dashboard/customer/appointments/${appointment.id}`);
  };
  
  // Handle adding appointment to calendar
  // Toggle calendar dropdown for a specific appointment
  const toggleCalendarDropdown = (appointmentId: string) => {
    setOpenCalendarDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  // Generate Google Calendar URL
  const getGoogleCalendarUrl = (appointment: Appointment) => {
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return '';
    const startDate = new Date(startIso);
    const totalDuration = getTotalDuration(appointment) || 60;
    const endDate = new Date(startDate.getTime() + totalDuration * 60000);
    
    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
    const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
    const location = appointment.business_name || '';
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${serviceNames} at ${appointment.business_name}`,
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
      details: description,
      location: location
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Generate Outlook Calendar URL
  const getOutlookCalendarUrl = (appointment: Appointment) => {
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return '';
    const startDate = new Date(startIso);
    const totalDuration = getTotalDuration(appointment) || 60;
    const endDate = new Date(startDate.getTime() + totalDuration * 60000);
    
    const formatOutlookDate = (date: Date) => {
      return date.toISOString();
    };
    
    const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
    const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
    const location = appointment.business_name || '';
    
    const params = new URLSearchParams({
      subject: `${serviceNames} at ${appointment.business_name}`,
      startdt: formatOutlookDate(startDate),
      enddt: formatOutlookDate(endDate),
      body: description,
      location: location
    });
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  // Download ICS file
  const downloadICSFile = (appointment: Appointment) => {
    try {
      const startIso = getAppointmentStartIso(appointment);
      if (!startIso) return;
      const startDate = new Date(startIso);
      const totalDuration = getTotalDuration(appointment) || 60;
      const endDate = new Date(startDate.getTime() + totalDuration * 60000);
      
      // Format dates for ICS file (YYYYMMDDTHHmmss)
      const formatICSDate = (date: Date) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };
      
      const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
      const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
      const location = appointment.business_name || '';
      
      // Generate ICS file content
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Appointment Booking System//EN',
        'BEGIN:VEVENT',
        `UID:${appointment.id}@appointment-booking`,
        `DTSTART:${formatICSDate(startDate)}`,
        `DTEND:${formatICSDate(endDate)}`,
        `SUMMARY:${serviceNames} at ${appointment.business_name}`,
        `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
        `LOCATION:${location}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT1H',
        'ACTION:DISPLAY',
        'DESCRIPTION:Reminder: Appointment in 1 hour',
        'END:VALARM',
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');
      
      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `appointment-${appointment.id}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success('Calendar event downloaded. Open it to add to your calendar.');
    } catch (error) {
      console.error('Error generating calendar file:', error);
      toast.error('Failed to generate calendar file. Please try again.');
    }
  };
  
  // Handle getting directions
  const handleGetDirections = async (appointment: Appointment) => {
    try {
      // Try to fetch business info to get address
      let address = '';
      if (appointment.business_short_name) {
        try {
          const business = await apiService.getBusiness(appointment.business_short_name);
          // Handle both nested and flat business object structures
          address = business?.business?.address || business?.address || '';
        } catch (error) {
          console.error('Error fetching business info:', error);
        }
      }
      
      // Construct Google Maps URL - use address only, fallback to business name if no address
      const query = address && address.trim() 
        ? encodeURIComponent(address.trim())
        : encodeURIComponent(appointment.business_name || '');
      
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
      
      // Open in new tab
      window.open(mapsUrl, '_blank');
    } catch (error) {
      console.error('Error getting directions:', error);
      toast.error('Failed to open directions. Please try again.');
    }
  };
  
  // Toggle manage dropdown for a specific appointment
  const toggleManageDropdown = (appointmentId: string) => {
    setOpenManageDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Render stars for rating display
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  // Check if any services in an appointment have been reviewed
  const hasAnyServicesReviewed = (appointment: Appointment) => {
    const reviews = appointmentReviews[appointment.id] || [];
    return reviews.length > 0;
  };

  // Check if appointment has any reviews (service reviews) or business ratings
  const hasAnyReviewsOrRatings = (appointment: Appointment) => {
    const reviews = appointmentReviews[appointment.id] || [];
    const businessRating = appointmentBusinessRatings[appointment.id];
    // Check if there are service reviews OR if business rating exists
    // businessRating will be null if no rating exists, or a BusinessRating object if it exists
    const hasReviews = reviews.length > 0;
    // Check if businessRating exists and is an object (not null or undefined)
    const hasRating = businessRating != null && typeof businessRating === 'object' && 'id' in businessRating;
    
    return hasReviews || hasRating;
  };

  const isAppointmentInPast = (appointment: Appointment) => {
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return false;
    const start = new Date(startIso);
    if (Number.isNaN(start.getTime())) return false;

    // Prefer explicit per-service end times when present (supports multi-professional + DST boundaries)
    const assignmentEndTimes = (appointment.assignments || [])
      .map(a => (a.toDateTime ? new Date(a.toDateTime) : null))
      .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));

    const end = assignmentEndTimes.length
      ? new Date(Math.max(...assignmentEndTimes.map(d => d.getTime())))
      : (() => {
          const totalMinutes = (appointment.assignments || []).reduce(
            (sum, a) => sum + (typeof a.durationMinutes === 'number' ? a.durationMinutes : 0),
            0
          );
          return totalMinutes > 0 ? new Date(start.getTime() + totalMinutes * 60000) : start;
        })();

    return end.getTime() < Date.now();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  /** Time string for display: single time or "Between x – y" when flex-only (no firm scheduledAt) */
  const getAppointmentTimeDisplay = (appointment: any) => {
    if (appointment?.scheduledAt) {
      return formatTime(appointment.scheduledAt);
    }
    const flex = appointment?.flexScheduledAt;
    if (flex?.earliest && flex?.latest) {
      return `Between ${formatTime(flex.earliest)} – ${formatTime(flex.latest)}`;
    }
    const iso = getAppointmentStartIso(appointment);
    return iso ? formatTime(iso) : '—';
  };

  const getStatusColor = (status: string, appointment?: { status?: string; seriesId?: string | null; series_id?: string | null; requiredDepositAmount?: number | string | null; required_deposit_amount?: number | string | null; receivedDepositAmount?: number | string | null; received_deposit_amount?: number | string | null }) => {
    if (status === 'PENDING' && appointment != null && isPendingSeriesDepositOutstanding(appointment, seriesDepositLookup)) {
      return 'bg-orange-100 text-orange-800';
    }
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING_QUOTE':
        return 'bg-orange-100 text-orange-800';
      case 'CONFIRMED':
        return 'bg-blue-50 text-blue-900';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const formatStatus = (status: string, appointment?: { status?: string; seriesId?: string | null; series_id?: string | null; requiredDepositAmount?: number | string | null; required_deposit_amount?: number | string | null; receivedDepositAmount?: number | string | null; received_deposit_amount?: number | string | null }) => {
    if (!status) return '';
    if (status === 'PENDING_QUOTE') return 'Pending Quote';
    if (status === 'PENDING' && appointment != null && isPendingSeriesDepositOutstanding(appointment, seriesDepositLookup)) {
      return 'Pending Deposit';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const isPendingDepositAppointment = (appointment: {
    status?: string;
    id?: string | number;
    seriesId?: string | null;
    series_id?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  }) => isPendingSeriesDepositOutstanding(appointment, seriesDepositLookup);

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'overview':
        const counts = getAppointmentCounts();
        const nextAppointment = getNextAppointment();
        
        return (
          <div className="space-y-4">
            {/* Welcome Section */}
            <div className="mb-4">
              <h1 className="text-xl font-bold mb-0">Welcome back, {user?.firstName || 'Customer'}! 👋</h1>
              <p className="text-gray-600 text-sm mb-0">Your dashboard at a glance.</p>
            </div>

            <CustomerPendingInvoicesAlert />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <Link
                to="/dashboard/customer/appointments"
                className="bg-white border border-gray-200 rounded-2xl p-3 flex items-center hover:shadow-md hover:-translate-y-0.5 transition-all text-decoration-none text-gray-900"
                style={{ background: 'linear-gradient(to right, #ffffff, #eff6ff)' }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-base mr-3 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}
                >
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                    Upcoming
                  </div>
                  <div className="text-2xl font-bold leading-tight mt-1">
                    {counts.upcoming} <span className="text-base font-normal text-gray-600">visits</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/dashboard/customer/quote-requests"
                className="bg-white border border-gray-200 rounded-2xl p-3 flex items-center hover:shadow-md hover:-translate-y-0.5 transition-all text-decoration-none text-gray-900"
                style={{ background: 'linear-gradient(to right, #ffffff, #f5f3ff)' }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-base mr-3 flex-shrink-0"
                  style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#7C3AED' }}
                >
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-violet-600 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                    Service Quotes
                  </div>
                  <div className="text-2xl font-bold leading-tight mt-1">
                    {customerQuotesLoading ? '...' : customerQuotes.length} <span className="text-base font-normal text-gray-600">quote requests</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/search"
                className="bg-white border border-gray-200 rounded-2xl p-3 flex items-center justify-between hover:shadow-md hover:-translate-y-0.5 transition-all text-decoration-none text-gray-900"
                style={{ background: 'linear-gradient(to right, #ffffff, #f0fdf4)' }}
              >
                <div className="flex items-center">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-base mr-3 flex-shrink-0 text-white shadow-sm"
                    style={{ backgroundColor: '#10b981' }}
                  >
                    <Plus className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-green-600 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                      Quick Action
                    </div>
                    <div className="text-lg font-bold leading-tight mt-1">
                      Book Now
                    </div>
                  </div>
                </div>
                <div className="text-gray-400">
                  <ChevronRight className="w-5 h-5" />
                </div>
              </Link>
            </div>

            {/* Next Appointment and Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 mb-6">
              {/* Next Appointment Card */}
              <div className="lg:col-span-4">
                <h2 className="text-xs text-gray-500 font-bold uppercase mb-3 tracking-wider" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>
                  Next Appointment
                </h2>
                {nextAppointment ? (
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm h-full">
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            {getAppointmentStartIso(nextAppointment) ? formatDate(getAppointmentStartIso(nextAppointment)!) : 'Date TBD'}
                          </div>
                          <div className="text-base font-semibold flex items-center mt-1" style={{ color: '#0B4778' }}>
                            <Clock className="w-4 h-4 mr-1" />
                            {getAppointmentTimeDisplay(nextAppointment)}
                            {(!!(nextAppointment as any)?.scheduledAt ||
                              !((nextAppointment as any)?.flexScheduledAt?.earliest && (nextAppointment as any)?.flexScheduledAt?.latest)) &&
                              nextAppointment.assignments &&
                              nextAppointment.assignments.length > 0 &&
                              (() => {
                                const totalDuration = getTotalDuration(nextAppointment);
                                const nextStartIso = getAppointmentStartIso(nextAppointment);
                                const startTime = nextStartIso ? new Date(nextStartIso) : null;
                                if (!startTime) return null;
                                const endTime = new Date(startTime.getTime() + totalDuration * 60000);
                                return (
                                  <span>
                                    {'\u00A0-\u00A0'}
                                    {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </span>
                                );
                              })()}
                            {Boolean((nextAppointment as any)?.seriesId || (nextAppointment as any)?.series_id) ? (
                              <span
                                className="inline-flex items-center gap-1 ml-2 text-xs font-bold px-2 py-0.5 rounded-full"
                                aria-label="Recurring appointment"
                                title="Recurring appointment"
                                style={{ color: '#0B4778', backgroundColor: 'rgba(11, 71, 120, 0.10)' }}
                              >
                                <Repeat className="w-3.5 h-3.5" aria-hidden style={{ color: '#0B4778' }} />
                                <span>Recurring</span>
                              </span>
                            ) : null}
                          </div>

                          {/* Recurring series details (optional) */}
                          {(() => {
                            const seriesId = (nextAppointment as any)?.seriesId || (nextAppointment as any)?.series_id;
                            if (!seriesId) return null;

                            const intervalWeeksRaw =
                              (nextAppointment as any)?.seriesIntervalWeeks ??
                              (nextAppointment as any)?.series_interval_weeks;
                            const occurrenceIndexRaw =
                              (nextAppointment as any)?.occurrenceIndex ??
                              (nextAppointment as any)?.occurrence_index;
                            const occurrenceCountRaw =
                              (nextAppointment as any)?.seriesOccurrenceCount ??
                              (nextAppointment as any)?.series_occurrence_count;

                            const intervalWeeks = Number(intervalWeeksRaw);
                            const occurrenceIndex = Number(occurrenceIndexRaw);
                            const occurrenceCount = Number(occurrenceCountRaw);

                            const list = allAppointments.length > 0 ? allAppointments : appointments;
                            const nextStartIso = getAppointmentStartIso(nextAppointment);
                            const currentMs = nextStartIso ? new Date(nextStartIso).getTime() : 0;
                            const nextInSeries = list
                              .filter((a: any) => (a?.seriesId || a?.series_id) === seriesId)
                              .filter((a: any) => a?.status !== 'CANCELLED')
                              .filter((a: any) => (getAppointmentStartIso(a) ? new Date(getAppointmentStartIso(a)!).getTime() : 0) > currentMs)
                              .sort((a: any, b: any) => (getAppointmentStartIso(a) ? new Date(getAppointmentStartIso(a)!).getTime() : 0) - (getAppointmentStartIso(b) ? new Date(getAppointmentStartIso(b)!).getTime() : 0))[0];

                            const everyLabel =
                              Number.isFinite(intervalWeeks) && intervalWeeks > 0
                                ? `Every ${intervalWeeks} ${intervalWeeks === 1 ? 'week' : 'weeks'}`
                                : null;

                            const indexLabel =
                              Number.isFinite(occurrenceIndex) &&
                              occurrenceIndex > 0 &&
                              Number.isFinite(occurrenceCount) &&
                              occurrenceCount > 0
                                ? `${occurrenceIndex} of ${occurrenceCount} in series`
                                : null;

                            const nextLabel = (() => {
                              const nextSeriesIso = getAppointmentStartIso(nextInSeries);
                              if (!nextSeriesIso) return null;
                              const d = new Date(nextSeriesIso);
                              if (Number.isNaN(d.getTime())) return null;
                              const datePart = d.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                              const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              return `Next: ${datePart} • ${timePart}`;
                            })();

                            const pillClass =
                              'inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700';

                            return (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {everyLabel && (
                                  <span className={pillClass}>
                                    <Repeat className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                    <span>{everyLabel}</span>
                                  </span>
                                )}
                                {indexLabel && (
                                  <span className={pillClass}>
                                    <ListOrdered className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                    <span>{indexLabel}</span>
                                  </span>
                                )}
                                {nextLabel && (
                                  <span className={pillClass}>
                                    <Calendar className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                    <span>{nextLabel}</span>
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {nextAppointment.invoiceId && nextAppointment.invoiceStatus !== 'DRAFT' && nextAppointment.invoiceStatus !== 'VOIDED' && (
                            <Link
                              to={`/dashboard/customer/invoices/${nextAppointment.invoiceId}`}
                              className="inline-flex items-center justify-center w-8 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                              title="View invoice"
                              aria-label="View invoice"
                            >
                              <FileText className="w-4 h-4" />
                            </Link>
                          )}
                          {isPendingDepositAppointment(nextAppointment) ? (
                            <Link
                              to={`/dashboard/customer/appointments/${nextAppointment.id}/deposit`}
                              className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(nextAppointment.status, nextAppointment)} hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-1`}
                              aria-label="Pay deposit"
                            >
                              {formatStatus(nextAppointment.status, nextAppointment)}
                            </Link>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(nextAppointment.status, nextAppointment)}`}>
                              {formatStatus(nextAppointment.status, nextAppointment)}
                            </span>
                          )}
                        </div>
                      </div>

                      <hr className="border-gray-200 opacity-25 my-3" />

                      <div className="mb-4">
                        <div className="text-xs text-gray-500 mb-1">Business</div>
                        <div className="flex items-center font-medium">
                          <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                          {nextAppointment.business_short_name ? (
                            <Link 
                              to={`/${nextAppointment.business_short_name}`}
                              className="hover:underline transition-colors"
                              style={{ color: '#0B4778' }}
                            >
                              {nextAppointment.business_name}
                            </Link>
                          ) : (
                            <span>{nextAppointment.business_name}</span>
                          )}
                        </div>
                      </div>

                      {/* Services Section */}
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 mb-2">Services</div>
                        <div className="bg-gray-50 rounded-xl p-3">
                          {nextAppointment.assignments && nextAppointment.assignments.length > 0 ? (
                            <>
                              <div className="space-y-3 mb-2">
                                {nextAppointment.assignments.map((assignment, idx) => {
                                  const isPendingQuote = nextAppointment.status === 'PENDING_QUOTE';
                                  const rawPrice = (assignment as any)?.price;
                                  const price =
                                    typeof rawPrice === 'number'
                                      ? rawPrice
                                      : (typeof rawPrice === 'string' ? parseFloat(rawPrice) : 0);
                                  const isQuotableService = (assignment as any)?.serviceQuotable === true;
                                  const isUnknownPrice =
                                    isPendingQuote ||
                                    rawPrice === null ||
                                    rawPrice === undefined ||
                                    (isQuotableService && (price === 0 || rawPrice == null || rawPrice === ''));
                                  const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                    ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                    : 'Any Professional';
                                  return (
                                    <div key={idx} className="flex justify-between items-start">
                                      <div className="flex flex-col gap-1">
                                        <span className="font-medium text-gray-900 text-sm">{assignment.serviceName}</span>
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                          <User className="w-3 h-3" />
                                          <span>{professionalName}</span>
                                        </div>
                                      </div>
                                      {isUnknownPrice ? (
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700" title="Quotable price">
                                          <HelpCircle className="w-4 h-4" />
                                        </span>
                                      ) : (
                                        <span className="text-gray-600 text-sm">${price.toFixed(2)}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="border-t border-gray-200 pt-2 mt-2">
                                <div className="flex justify-between items-center font-bold text-sm">
                                  <span>Total Est.</span>
                                  {nextAppointment.status === 'PENDING_QUOTE' ? (
                                    <span className="inline-flex items-center gap-1">
                                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700" title="Total pending quote">
                                        <HelpCircle className="w-4 h-4" />
                                      </span>
                                    </span>
                                  ) : nextAppointment.quoteId && nextAppointment.quoteFinalAgreedAmount != null ? (
                                    <span className="flex items-center gap-1">
                                      ${Number(nextAppointment.quoteFinalAgreedAmount).toFixed(2)}
                                      <span className="text-red-500">*</span>
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      ${nextAppointment.assignments.reduce((total, assignment) => {
                                        const raw = (assignment as any)?.price;
                                        const p =
                                          typeof raw === 'number'
                                            ? raw
                                            : (typeof raw === 'string' ? parseFloat(raw) : 0);
                                        return total + (Number.isFinite(p) ? p : 0);
                                      }, 0).toFixed(2)}
                                      <span className="text-red-500">*</span>
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-start gap-2 mt-2">
                                  <Info className="w-3 h-3 text-gray-500 flex-shrink-0 mt-0.5" />
                                  <p className="text-xs text-gray-600">Prices are estimates and may change based on the final services provided.</p>
                                </div>
                                {nextAppointment.receivedDepositAmount != null && Number(nextAppointment.receivedDepositAmount) > 0 && (
                                  <div className="flex flex-col gap-0.5 mt-2 pt-2 border-t border-gray-100">
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-gray-600">Deposit paid</span>
                                      <span className="font-medium text-gray-900">${Number(nextAppointment.receivedDepositAmount).toFixed(2)}</span>
                                    </div>
                                    {nextAppointment.depositPaidAt && (
                                      <div className="text-xs text-gray-500">
                                        {formatDate(nextAppointment.depositPaidAt)} at {formatTime(nextAppointment.depositPaidAt)}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-gray-500">No services assigned</div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2">
                        <Link
                          to={`/dashboard/customer/appointments/${nextAppointment.id}`}
                          className="aacore-btn-primary w-full sm:w-auto justify-center"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </Link>
                        {nextAppointment.status === 'COMPLETED' ||
                        (isAppointmentInPast(nextAppointment) && nextAppointment.status !== 'CANCELLED') ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRebookAppointment(nextAppointment);
                            }}
                            className="aacore-btn2-primary rounded-full flex-1 sm:flex-none"
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Rebook
                          </button>
                        ) : (
                          <div className="relative w-full sm:w-auto" ref={manageDropdownRef}>
                            <button 
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsManageDropdownOpen(prev => !prev);
                              }}
                              className="aacore-btn-outlined w-full sm:w-auto justify-center cursor-pointer relative z-10"
                            >
                              <Settings className="w-4 h-4" />
                              Manage <ChevronDown className={`w-4 h-4 transition-transform ${isManageDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isManageDropdownOpen && (
                              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-hidden">
                                {isPendingDepositAppointment(nextAppointment) && (
                                  <Link
                                    to={`/dashboard/customer/appointments/${nextAppointment.id}/deposit`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsManageDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                                  >
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Deposit
                                  </Link>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsManageDropdownOpen(false);
                                    handleRescheduleAppointment(nextAppointment);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Reschedule Appointment
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsManageDropdownOpen(false);
                                    handleCancelAppointment(nextAppointment);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Cancel appointment
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 text-center h-full flex flex-col items-center justify-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">No upcoming appointments</p>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="lg:col-span-3 mt-6 lg:mt-0">
                <h2 className="text-xs text-gray-500 font-bold uppercase mb-3 tracking-wider" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>
                  Recent Activity
                </h2>
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm h-full">
                  {notifications.length > 0 ? (
                    <div className="p-0">
                      <ul className="list-none pt-2">
                        {/* Recent notifications/activities */}
                        {notifications.slice(0, 4).map((notification) => (
                          <li key={notification.id} className="p-3 border-b border-gray-100 last:border-0 flex gap-3 items-start">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
                              style={{ 
                                backgroundColor: notification.type === 'APPOINTMENT_BOOKED' ? 'rgba(16, 185, 129, 0.1)' : 
                                               notification.type === 'APPOINTMENT_CANCELLED' ? 'rgba(239, 68, 68, 0.1)' :
                                               notification.type === 'APPOINTMENT_RESCHEDULED' ? 'rgba(11, 71, 120, 0.1)' :
                                               notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED' ? 'rgba(245, 158, 11, 0.1)' :
                                               notification.type === 'QUOTE_REQUEST_SUBMITTED' ? 'rgba(11, 71, 120, 0.1)' :
                                               notification.type === 'GIFT_CARD_REDEEMED' ? 'rgba(245, 158, 11, 0.1)' : 
                                               'rgba(107, 114, 128, 0.1)',
                                color: notification.type === 'APPOINTMENT_BOOKED' ? '#10b981' : 
                                       notification.type === 'APPOINTMENT_CANCELLED' ? '#EF4444' :
                                       notification.type === 'APPOINTMENT_RESCHEDULED' ? '#0B4778' :
                                       notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED' ? '#F59E0B' :
                                       notification.type === 'QUOTE_REQUEST_SUBMITTED' ? '#0B4778' :
                                       notification.type === 'GIFT_CARD_REDEEMED' ? '#F59E0B' : 
                                       '#6B7280'
                              }}
                            >
                              {notification.type === 'APPOINTMENT_BOOKED' ? (
                                <Check className="w-4 h-4" />
                              ) : notification.type === 'APPOINTMENT_CANCELLED' ? (
                                <XCircle className="w-4 h-4" />
                              ) : notification.type === 'APPOINTMENT_RESCHEDULED' ? (
                                <CalendarClock className="w-4 h-4" />
                              ) : notification.type === 'APPOINTMENT_DEPOSIT_REQUESTED' ? (
                                <DollarSign className="w-4 h-4" />
                              ) : notification.type === 'QUOTE_REQUEST_SUBMITTED' ? (
                                <FileText className="w-4 h-4" />
                              ) : notification.type === 'GIFT_CARD_REDEEMED' ? (
                                <Gift className="w-4 h-4" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-500 mb-0">
                                {new Date(notification.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {new Date(notification.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </div>
                              <Link
                                to={getCustomerNotificationHref(notification)}
                                className="font-medium text-sm text-[#0B4778] hover:underline transition-colors"
                              >
                                {notification.title}
                              </Link>
                              <div className="text-xs text-gray-500">{notification.message}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {notifications.length > 4 && (
                        <div className="py-0.5 px-2 text-center border-t border-gray-100">
                          <button 
                            onClick={() => navigate('/dashboard/customer/notifications')}
                            className="text-xs font-medium inline-flex items-center gap-1"
                            style={{ color: '#0B4778' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
                          >
                            View all history
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-8 text-center h-full flex flex-col items-center justify-center">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 text-sm">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recently Visited Businesses (full-width row below cards) */}
              {(() => {
                const recentlyVisited = getRecentlyVisitedBusinesses();
                if (recentlyVisited.length === 0) return null;

                return (
                  <div className="lg:col-span-7 mt-6">
                    <h2
                      className="text-xs text-gray-500 font-bold uppercase mb-3 tracking-wider"
                      style={{ fontSize: '0.7rem', letterSpacing: '1px' }}
                    >
                      Recently Visited
                    </h2>
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
                      <div className="p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {recentlyVisited.slice(0, 2).map((business) => (
                            <div
                              key={business.businessId}
                              className="border rounded-xl p-3 flex flex-col sm:flex-row justify-between items-center gap-3 bg-gray-50"
                            >
                              <div className="flex items-center gap-3">
                                <div className="bg-white border rounded-full p-2 shadow-sm text-[#0B4778]">
                                  <Building2 className="w-5 h-5" />
                                </div>
                                <div>
                                  <div className="font-bold text-gray-900">{business.businessName}</div>
                                  <div className="text-xs text-gray-500">
                                    {formatAppointmentSubtext(business.lastAppointmentDate)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-row gap-2 w-full sm:w-auto">
                                <Link
                                  to={`/dashboard/customer/appointments?businessId=${business.businessId}`}
                                  className="aacore-btn-outlined flex-1 sm:flex-none sm:w-auto justify-center"
                                >
                                  <Clock className="w-4 h-4" />
                                  History
                                </Link>
                                <Link
                                  to={`/${business.businessShortName}`}
                                  className="aacore-btn-primary flex-1 sm:flex-none sm:w-auto justify-center"
                                >
                                  <CalendarPlus className="w-4 h-4" />
                                  Book
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
        

      case 'appointments':
        // Get all filtered appointments
        const allFiltered = getFilteredAppointments();
        const hasFilters = selectedBusinessId || selectedProfessionalId || searchQuery.trim();
        
        // If filters are applied, use client-side pagination
        let filteredAppointments: Appointment[];
        let pagination: typeof appointmentPagination;
        
        if (hasFilters) {
          // Client-side pagination for filtered results
          const startIndex = (filteredPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          filteredAppointments = allFiltered.slice(startIndex, endIndex);
          
          const totalPages = Math.ceil(allFiltered.length / itemsPerPage);
          pagination = {
            currentPage: filteredPage,
            totalPages: totalPages || 1,
            total: allFiltered.length,
            hasNextPage: filteredPage < totalPages,
            hasPrevPage: filteredPage > 1
          };
        } else {
          // No filters - use server-side pagination
          filteredAppointments = allFiltered;
          pagination = appointmentPagination;
        }
        
        // Helper function to get status icon and color
        const getStatusIcon = (status: string) => {
          switch (status) {
            case 'CONFIRMED':
              return { icon: CheckCircle, color: 'bg-blue-50 text-blue-900' };
            case 'PENDING':
              return { icon: Clock, color: 'bg-yellow-100 text-yellow-600' };
            case 'COMPLETED':
              return { icon: CheckCircle, color: 'bg-green-100 text-green-600' };
            case 'CANCELLED':
              return { icon: XCircle, color: 'bg-red-100 text-red-600' };
            default:
              return { icon: Calendar, color: 'bg-gray-100 text-gray-600' };
          }
        };

        const getNextInSeries = (current: Appointment, list: Appointment[]) => {
          const seriesId = (current as any)?.seriesId ?? (current as any)?.series_id;
          if (!seriesId) return null;

          const currentStartIso = getAppointmentStartIso(current);
          if (!currentStartIso) return null;
          const currentStart = new Date(currentStartIso);
          if (Number.isNaN(currentStart.getTime())) return null;

          return (
            list
              .filter((a) => ((a as any)?.seriesId ?? (a as any)?.series_id) === seriesId && a.status !== 'CANCELLED')
              .sort((a, b) => (getAppointmentStartIso(a) ? new Date(getAppointmentStartIso(a)!).getTime() : 0) - (getAppointmentStartIso(b) ? new Date(getAppointmentStartIso(b)!).getTime() : 0))
              .find((a) => (getAppointmentStartIso(a) ? new Date(getAppointmentStartIso(a)!).getTime() : 0) > currentStart.getTime()) || null
          );
        };

        return (
          <div className="space-y-4">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>

            {/* Page Header */}
            <div className="mb-4">
              <h1 className="text-xl font-bold mb-0">My Service Appointments</h1>
              <p className="text-gray-600 text-sm mb-0">View & manage your appointments</p>
            </div>

            {/* Additional Filters - Business, Professional, Search - Desktop Only */}
            <div className="hidden md:block mb-4 space-y-3">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Business Filter Dropdown */}
                <div className="relative flex-1" ref={businessDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsBusinessDropdownOpen(!isBusinessDropdownOpen)}
                    className="aacore-dropdown-primary"
                  >
                    <span className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedBusinessId 
                        ? getUniqueBusinesses().find(b => b.id === selectedBusinessId)?.name || 'All Businesses'
                        : 'All Businesses'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isBusinessDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isBusinessDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedBusinessId('');
                          setIsBusinessDropdownOpen(false);
                          setFilteredPage(1); // Reset to first page when clearing business filter
                        }}
                        className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                          !selectedBusinessId
                            ? 'bg-[#0B4778] text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        All Businesses
                      </button>
                      {getUniqueBusinesses().map((business) => (
                        <button
                          key={business.id}
                          type="button"
                          onClick={() => {
                            setSelectedBusinessId(business.id);
                            setIsBusinessDropdownOpen(false);
                            setFilteredPage(1); // Reset to first page when changing business filter
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            selectedBusinessId === business.id
                              ? 'bg-[#0B4778] text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {business.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Professional Filter Dropdown */}
                <div className="relative flex-1" ref={professionalDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsProfessionalDropdownOpen(!isProfessionalDropdownOpen)}
                    className="aacore-dropdown-primary"
                  >
                    <span className="flex items-center">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedProfessionalId 
                        ? getUniqueProfessionals().find(p => p.id === selectedProfessionalId)?.name || 'All Professionals'
                        : 'All Professionals'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isProfessionalDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isProfessionalDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedProfessionalId('');
                          setIsProfessionalDropdownOpen(false);
                          setFilteredPage(1); // Reset to first page when clearing professional filter
                        }}
                        className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                          !selectedProfessionalId
                            ? 'bg-[#0B4778] text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        All Professionals
                      </button>
                      {getUniqueProfessionals().map((professional) => (
                        <button
                          key={professional.id}
                          type="button"
                          onClick={() => {
                            setSelectedProfessionalId(professional.id);
                            setIsProfessionalDropdownOpen(false);
                            setFilteredPage(1); // Reset to first page when changing professional filter
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            selectedProfessionalId === professional.id
                              ? 'bg-[#0B4778] text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {professional.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Search Input */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search service name or notes..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setFilteredPage(1); // Reset to first page when search query changes
                    }}
                    className="aacore-textfield-primary pl-10"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XCircle className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Buttons - Desktop */}
            <div className="hidden md:flex flex-wrap gap-2">
                <button 
                  onClick={() => handleFilterChange('all')}
                  className={appointmentFilter === 'all' ? 'aacore-btn-primary' : 'aacore-btn-outlined'}
                >
                  <Grid className="w-4 h-4" />
                  All
                </button>
                <button 
                  onClick={() => handleFilterChange('upcoming')}
                  className={appointmentFilter === 'upcoming' ? 'aacore-btn-primary' : 'aacore-btn-outlined'}
                >
                  <Clock className="w-4 h-4" />
                  Upcoming
                </button>
                <button 
                  onClick={() => handleFilterChange('completed')}
                  className={appointmentFilter === 'completed' ? 'aacore-btn-primary' : 'aacore-btn-outlined'}
                >
                  <CheckCircle className="w-4 h-4" />
                  Past
                </button>
                <button 
                  onClick={() => handleFilterChange('cancelled')}
                  className={appointmentFilter === 'cancelled' ? 'aacore-btn-primary' : 'aacore-btn-outlined'}
                >
                  <XCircle className="w-4 h-4" />
                  Canceled
                </button>
            </div>

            {/* Filter Dropdown - Mobile */}
            <div className="md:hidden relative mb-3" ref={filterDropdownRef}>
              <button
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                className="aacore-dropdown-primary"
              >
                <span className="flex items-center gap-2">
                  {appointmentFilter === 'all' ? (
                    <>
                      <Grid className="w-4 h-4" />
                      All
                    </>
                  ) : appointmentFilter === 'upcoming' ? (
                    <>
                      <Clock className="w-4 h-4" />
                      Upcoming
                    </>
                  ) : appointmentFilter === 'completed' ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Past
                    </>
                  ) : appointmentFilter === 'cancelled' ? (
                    <>
                      <XCircle className="w-4 h-4" />
                      Canceled
                    </>
                  ) : (
                    <>
                      <Grid className="w-4 h-4" />
                      All
                    </>
                  )}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                  <button
                    onClick={() => {
                      handleFilterChange('all');
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                      appointmentFilter === 'all'
                        ? 'bg-[#0B4778] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                    All
                  </button>
                  <button
                    onClick={() => {
                      handleFilterChange('upcoming');
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                      appointmentFilter === 'upcoming'
                        ? 'bg-[#0B4778] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    Upcoming
                  </button>
                  <button
                    onClick={() => {
                      handleFilterChange('completed');
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                      appointmentFilter === 'completed'
                        ? 'bg-[#0B4778] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Past
                  </button>
                  <button
                    onClick={() => {
                      handleFilterChange('cancelled');
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors rounded-b-md flex items-center gap-2 ${
                      appointmentFilter === 'cancelled'
                        ? 'bg-[#0B4778] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <XCircle className="w-4 h-4" />
                    Canceled
                  </button>
                </div>
              )}
            </div>
            
            {/* Collapsible Filter Section - Mobile */}
            <div className="md:hidden mb-4">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-visible">
                <button
                  onClick={() => setIsFilterSectionOpen(!isFilterSectionOpen)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                    {(selectedBusinessId || selectedProfessionalId || searchQuery.trim()) && (
                      <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                        {[selectedBusinessId, selectedProfessionalId, searchQuery.trim()].filter(Boolean).length}
                      </span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isFilterSectionOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isFilterSectionOpen && (
                  <div className="px-4 pb-4 pt-2 space-y-3 border-t border-gray-200">
                  {/* Business Filter Dropdown */}
                  <div className="relative" ref={businessDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsBusinessDropdownOpen(!isBusinessDropdownOpen)}
                      className="aacore-dropdown-primary w-full"
                    >
                      <span className="flex items-center">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        {selectedBusinessId 
                          ? getUniqueBusinesses().find(b => b.id === selectedBusinessId)?.name || 'All Businesses'
                          : 'All Businesses'}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isBusinessDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isBusinessDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedBusinessId('');
                            setIsBusinessDropdownOpen(false);
                            setFilteredPage(1);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            !selectedBusinessId
                              ? 'bg-[#0B4778] text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          All Businesses
                        </button>
                        {getUniqueBusinesses().map((business) => (
                          <button
                            key={business.id}
                            type="button"
                            onClick={() => {
                              setSelectedBusinessId(business.id);
                              setIsBusinessDropdownOpen(false);
                              setFilteredPage(1);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                              selectedBusinessId === business.id
                                ? 'bg-[#0B4778] text-white'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {business.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Professional Filter Dropdown */}
                  <div className="relative" ref={professionalDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsProfessionalDropdownOpen(!isProfessionalDropdownOpen)}
                      className="aacore-dropdown-primary w-full"
                    >
                      <span className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        {selectedProfessionalId 
                          ? getUniqueProfessionals().find(p => p.id === selectedProfessionalId)?.name || 'All Professionals'
                          : 'All Professionals'}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isProfessionalDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isProfessionalDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProfessionalId('');
                            setIsProfessionalDropdownOpen(false);
                            setFilteredPage(1);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            !selectedProfessionalId
                              ? 'bg-[#0B4778] text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          All Professionals
                        </button>
                        {getUniqueProfessionals().map((professional) => (
                          <button
                            key={professional.id}
                            type="button"
                            onClick={() => {
                              setSelectedProfessionalId(professional.id);
                              setIsProfessionalDropdownOpen(false);
                              setFilteredPage(1);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                              selectedProfessionalId === professional.id
                                ? 'bg-[#0B4778] text-white'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {professional.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search service name or notes..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setFilteredPage(1);
                      }}
                      className="aacore-textfield-primary pl-10"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <XCircle className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
            
            {appointmentsLoading ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-900">Loading appointments...</p>
                </div>
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No appointments found</h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    {selectedBusinessId || selectedProfessionalId || searchQuery.trim()
                      ? 'No appointments match your current filters. Try adjusting your search criteria.'
                      : appointmentFilter === 'all' 
                        ? 'Start booking appointments with service providers to see them here.'
                        : `No ${appointmentFilter} appointments found.`
                    }
                  </p>
                  {(selectedBusinessId || selectedProfessionalId || searchQuery.trim()) && (
                    <button
                      onClick={() => {
                        setSelectedBusinessId('');
                        setSelectedProfessionalId('');
                        setSearchQuery('');
                        setFilteredPage(1); // Reset to first page when clearing filters
                      }}
                      className="mt-4 aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4 overflow-visible">
                  {filteredAppointments.map((appointment) => {
                    const { icon: StatusIcon, color: statusColor } = getStatusIcon(appointment.status);
                    const totalDuration = getTotalDuration(appointment) || 0;
                    const totalPrice = appointment.assignments?.reduce((total, assignment) => {
                                let price = 0;
                                if (typeof assignment.price === 'number') {
                                  price = assignment.price;
                                } else if (typeof assignment.price === 'string') {
                                  price = parseFloat(assignment.price) || 0;
                                }
                                return total + price;
                    }, 0) || 0;
                    const hasQuotableService = appointment.assignments?.some((a: any) => a.serviceQuotable === true) ?? false;
                    const offerAmount = (appointment as any).quoteFinalAgreedAmount;
                    const estimatedTotal = hasQuotableService && offerAmount != null && Number.isFinite(Number(offerAmount))
                      ? Number(offerAmount)
                      : totalPrice;
                    const professionals = appointment.assignments?.map(assignment => 
                                  `${assignment.professionalFirstName} ${assignment.professionalLastName}`
                    ).filter(Boolean).join(', ') || '';

                    const isNewlyBooked = newlyBookedAppointmentIds.has(String(appointment.id));
                    const isExpanded = expandedAppointmentIds.has(String(appointment.id));
                    const hasViewableInvoice = appointment.invoiceId && appointment.invoiceStatus !== 'DRAFT' && appointment.invoiceStatus !== 'VOIDED';
                    const isPendingQuote = appointment.status === 'PENDING_QUOTE';
                    const seriesId = (appointment as any)?.seriesId ?? (appointment as any)?.series_id;
                    const seriesList = (allAppointments && allAppointments.length > 0 ? allAppointments : allFiltered) as Appointment[];
                    const nextInSeries = seriesId ? getNextInSeries(appointment, seriesList) : null;

                    const pendingQuoteCardStyle = isPendingQuote
                      ? ({
                          backgroundColor: '#FFFEFD', // near-white warm tint
                          // Light orange zig-zag / chevron effect via overlapping repeating gradients
                          backgroundImage:
                            'repeating-linear-gradient(135deg, rgba(251, 146, 60, 0.02) 0px, rgba(251, 146, 60, 0.02) 6px, transparent 6px, transparent 18px), repeating-linear-gradient(225deg, rgba(251, 146, 60, 0.02) 0px, rgba(251, 146, 60, 0.02) 6px, transparent 6px, transparent 18px)',
                          backgroundSize: '18px 18px',
                          backgroundPosition: '0 0, 9px 9px'
                        } as React.CSSProperties)
                      : undefined;
                    
                    return (
                      <div 
                        key={appointment.id} 
                        onClick={() => toggleAppointmentCard(appointment.id)}
                        style={pendingQuoteCardStyle}
                        className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-visible cursor-pointer ${
                          isNewlyBooked ? 'border-green-300 border-2' : isPendingQuote ? 'border-orange-200' : 'border-gray-200'
                        }`}
                      >
                        <div className="px-6 pt-6 pb-4 overflow-visible">
                          <div className="flex items-start gap-4">
                            <div className="flex-1 min-w-0 relative">
                              <div className="absolute top-0 right-0 flex flex-col gap-1.5 items-end">
                                {isNewlyBooked && (
                                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full flex-shrink-0 bg-green-100 text-green-800 border border-green-300">
                                    Newly Booked
                                  </span>
                                )}
                                <div className="flex items-center gap-1.5">
                                  {hasViewableInvoice && (
                                    <Link
                                      to={`/dashboard/customer/invoices/${appointment.invoiceId}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center justify-center w-8 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                                      title="View invoice"
                                      aria-label="View invoice"
                                    >
                                      <FileText className="w-4 h-4" />
                                    </Link>
                                  )}
                                  {isPendingDepositAppointment(appointment) ? (
                                    <Link
                                      to={`/dashboard/customer/appointments/${appointment.id}/deposit`}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`px-2.5 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusColor(appointment.status, appointment)} hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-1`}
                                      aria-label="Pay deposit"
                                    >
                                      {formatStatus(appointment.status, appointment)}
                                    </Link>
                                  ) : (
                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusColor(appointment.status, appointment)}`}>
                                      {formatStatus(appointment.status, appointment)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={`${isNewlyBooked ? 'pr-28' : hasViewableInvoice ? 'pr-28' : 'pr-24'}`}>
                                <div className="mb-2">
                                  <div className="text-lg font-bold text-gray-900">
                                    {getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}
                                  </div>
                                  <div className="text-sm font-semibold flex items-center mt-1" style={{ color: '#0B4778' }}>
                                    <Clock className="w-4 h-4 mr-1" />
                                    {getAppointmentTimeDisplay(appointment)}
                                    {(!!(appointment as any)?.scheduledAt ||
                                      !((appointment as any)?.flexScheduledAt?.earliest && (appointment as any)?.flexScheduledAt?.latest)) &&
                                      totalDuration > 0 &&
                                      (() => {
                                        const aptStartIso = getAppointmentStartIso(appointment);
                                        const startTime = aptStartIso ? new Date(aptStartIso) : null;
                                        if (!startTime) return null;
                                        const endTime = new Date(startTime.getTime() + totalDuration * 60000);
                                        return (
                                          <span>
                                            {'\u00A0-\u00A0'}
                                            {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                          </span>
                                        );
                                      })()}
                                    {seriesId ? (
                                      <span
                                        className="inline-flex items-center gap-1 ml-2 text-xs font-bold px-2 py-0.5 rounded-full"
                                        aria-label="Recurring appointment"
                                        title="Recurring appointment"
                                        style={{ color: '#0B4778', backgroundColor: 'rgba(11, 71, 120, 0.10)' }}
                                      >
                                        <Repeat className="w-3.5 h-3.5" aria-hidden style={{ color: '#0B4778' }} />
                                        <span>Recurring</span>
                                      </span>
                                    ) : null}
                                  </div>

                                  {(() => {
                                    if (!seriesId) return null;

                                    const intervalWeeksRaw =
                                      (appointment as any)?.seriesIntervalWeeks ??
                                      (appointment as any)?.series_interval_weeks;
                                    const occurrenceIndexRaw =
                                      (appointment as any)?.occurrenceIndex ??
                                      (appointment as any)?.occurrence_index;
                                    const occurrenceCountRaw =
                                      (appointment as any)?.seriesOccurrenceCount ??
                                      (appointment as any)?.series_occurrence_count;

                                    const intervalWeeks = Number(intervalWeeksRaw);
                                    const occurrenceIndex = Number(occurrenceIndexRaw);
                                    const occurrenceCount = Number(occurrenceCountRaw);

                                    const everyLabel =
                                      Number.isFinite(intervalWeeks) && intervalWeeks > 0
                                        ? `Every ${intervalWeeks} ${intervalWeeks === 1 ? 'week' : 'weeks'}`
                                        : null;

                                    const indexLabel =
                                      Number.isFinite(occurrenceIndex) &&
                                      occurrenceIndex > 0 &&
                                      Number.isFinite(occurrenceCount) &&
                                      occurrenceCount > 0
                                        ? `${occurrenceIndex} of ${occurrenceCount} in series`
                                        : null;

                                    const nextLabel = (() => {
                                      if (!nextInSeries?.scheduledAt) return null;
                                      const d = new Date(nextInSeries.scheduledAt);
                                      if (Number.isNaN(d.getTime())) return null;
                                      const datePart = d.toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      });
                                      const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                      return `Next: ${datePart} • ${timePart}`;
                                    })();

                                    const pillClass =
                                      'inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700';

                                    return (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {everyLabel && (
                                          <span className={pillClass}>
                                            <Repeat className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                            <span>{everyLabel}</span>
                                          </span>
                                        )}
                                        {indexLabel && (
                                          <span className={pillClass}>
                                            <ListOrdered className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                            <span>{indexLabel}</span>
                                          </span>
                                        )}
                                        {nextLabel && (
                                          <span className={pillClass}>
                                            <Calendar className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                            <span>{nextLabel}</span>
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                                
                                {/* Business */}
                                <div className="flex items-start gap-2 mb-3">
                                  <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {appointment.business_short_name ? (
                                        <Link 
                                          to={`/${appointment.business_short_name}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-sm font-medium hover:underline transition-colors"
                                          style={{ color: '#0B4778' }}
                                        >
                                          {appointment.business_name}
                                        </Link>
                                      ) : (
                                        <p className="text-sm text-gray-900 font-medium">{appointment.business_name}</p>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGetDirections(appointment);
                                        }}
                                        className="relative group flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-blue-100 transition-colors"
                                        title="Get directions"
                                      >
                                        <Navigation className="w-3.5 h-3.5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                          Get directions
                                        </span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Collapsible Content */}
                              <div className={isExpanded ? 'block' : 'hidden'}>
                                  
                              {/* Services List */}
                              {appointment.assignments && appointment.assignments.length > 0 && (
                                    <div className="mb-4 pt-4 border-t border-gray-200 space-y-2">
                                      {appointment.assignments.map((assignment, index) => {
                                        const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                          ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                                          : assignment.professionalFirstName || assignment.professionalLastName || 'Any Professional';
                                        
                                        const rawPrice = (assignment as any)?.price;
                                        const servicePrice = typeof rawPrice === 'number'
                                          ? rawPrice
                                          : typeof rawPrice === 'string'
                                            ? parseFloat(rawPrice) || 0
                                            : 0;
                                        const isUnknownPrice = isPendingQuote || rawPrice === null || rawPrice === undefined;
                                        
                                        // Check if insistOnProfessional is true
                                        const isInsisted = assignment.insistOnProfessional === true;
                                        
                                        return (
                                          <div key={assignment.serviceId || index} className="flex items-start gap-2 py-1.5">
                                            <div className="flex-1">
                                              <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-medium text-gray-900">
                                                  {assignment.serviceName}
                                                </p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                  {isUnknownPrice ? (
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700" title="Quotable price">
                                                      <HelpCircle className="w-4 h-4" />
                                                    </span>
                                                  ) : (
                                                    <>${servicePrice.toFixed(2)}</>
                                                  )}
                                                </p>
                                              </div>
                                              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                Professional: {professionalName}
                                                {(() => {
                                                  const rawLoc = String((assignment as any)?.serviceLocation || '').toUpperCase().trim();
                                                  const isOnSite = rawLoc === 'ON_SITE' || rawLoc === 'ONSITE';
                                                  if (!isOnSite) return null;
                                                  return (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 ml-1">
                                                      On-Site
                                                    </span>
                                                  );
                                                })()}
                                                {isInsisted && (
                                                  <span className="inline-flex items-center gap-0.5 text-red-500" title="Customer insists on this professional">
                                                    <Heart className="w-3 h-3 fill-current" />
                                                    <span className="text-xs">Requested</span>
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Estimated Total */}
                                  {appointment.assignments && appointment.assignments.length > 0 && (
                                    <div className="mb-4 pt-3 border-t border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-semibold text-gray-700">Estimated Total</span>
                                        <span className="text-lg font-bold text-gray-900 flex items-center gap-1">
                                          {isPendingQuote ? (
                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700" title="Total pending quote">
                                              <HelpCircle className="w-5 h-5" />
                                            </span>
                                          ) : (
                                            <>
                                              ${estimatedTotal.toFixed(2)}
                                              <span className="text-red-500">*</span>
                                            </>
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex items-start gap-2 mt-2">
                                        <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-gray-600">Prices are estimates and may change based on the final services provided.</p>
                                      </div>
                                      {appointment.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                                        <div className="flex flex-col gap-0.5 mt-2 pt-2 border-t border-gray-100">
                                          <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-600">Deposit paid</span>
                                            <span className="font-medium text-gray-900">${Number(appointment.receivedDepositAmount).toFixed(2)}</span>
                                          </div>
                                          {appointment.depositPaidAt && (
                                            <div className="text-xs text-gray-500">
                                              {formatDate(appointment.depositPaidAt)} at {formatTime(appointment.depositPaidAt)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="pt-4 border-t border-gray-200">
                                    {/* Duration */}
                                    <div className="flex items-start gap-2 mb-4">
                                      <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="text-xs text-gray-500 mb-0.5">Duration</p>
                                        <p className="text-sm text-gray-900 font-medium">{totalDuration} minutes</p>
                                      </div>
                                    </div>

                                    {/* Service Location */}
                                    {(() => {
                                      const rawLocations = (appointment.assignments || [])
                                        .map((a: any) => String(a?.serviceLocation || '').toUpperCase().trim())
                                        .filter((x: string) => !!x);
                                      const hasOnSite = rawLocations.some((l: string) => l === 'ON_SITE' || l === 'ONSITE');
                                      const hasInStore = rawLocations.some((l: string) => l === 'IN_STORE' || l === 'INSTORE' || l === 'IN-STORE');

                                      if (!hasOnSite && !hasInStore) return null;

                                      const label =
                                        hasOnSite && hasInStore ? 'Mixed (In-store + On-site)'
                                        : hasOnSite ? 'On-site'
                                        : 'In-store';

                                      const addr = (appointment.assignments || []).find((a: any) => a?.serviceAddress)?.serviceAddress || null;
                                      const hasAddr =
                                        !!addr &&
                                        typeof addr.address === 'string' && addr.address.trim() &&
                                        typeof addr.city === 'string' && addr.city.trim() &&
                                        typeof addr.state === 'string' && addr.state.trim() &&
                                        typeof addr.zip === 'string' && addr.zip.trim();

                                      return (
                                        <div className="flex items-start gap-2 mb-4">
                                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                          <div>
                                            <p className="text-xs text-gray-500 mb-0.5">Service Location</p>
                                            <p className="text-sm text-gray-900 font-medium">{label}</p>
                                            {hasOnSite && hasAddr && (
                                              <div className="mt-2 text-sm text-gray-700">
                                                <div className="font-medium">{addr.address}</div>
                                                <div>
                                                  {addr.city}, {addr.state} {addr.zip}
                                                </div>
                                                {addr.locationNotes && String(addr.locationNotes).trim() !== '' && (
                                                  <div className="mt-2 text-gray-600">
                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                                                    <div className="mt-1">{addr.locationNotes}</div>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    
                                    {appointment.reminderSentAt && (
                                      <div className="flex items-start gap-2 mt-4">
                                        <Bell className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <p className="text-xs text-gray-500 mb-0.5">Reminder Sent</p>
                                          <p className="text-sm text-gray-900 font-medium">
                                            {formatDateTime(appointment.reminderSentAt)}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                              
                              {appointment.notes && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                                  <p className="text-sm text-gray-900">{appointment.notes}</p>
                                </div>
                              )}

                              {(() => {
                                const shortName = String((appointment as any)?.business_short_name || '').trim();
                                const msg = shortName ? customerAppointmentMessageCache[shortName] : undefined;
                                const show =
                                  Boolean(msg?.enabled) &&
                                  Boolean(msg?.message && String(msg.message).trim());
                                if (!show) return null;
                                return (
                                  <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                                    <div className="flex items-start gap-2">
                                      <MessageSquare className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-xs text-blue-800 font-semibold mb-1">
                                          Message from {appointment.business_name}
                                        </p>
                                        <p className="text-sm text-blue-900 whitespace-pre-line break-words">
                                          {String(msg!.message).trim()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Rating section for completed appointments */}
                              {appointment.status === 'COMPLETED' && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-900">Service Ratings</h4>
                                    {/* See my reviews button - only show if appointment is completed and has reviews */}
                                    {hasAnyServicesReviewed(appointment) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRateAppointment(appointment);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-900 hover:text-blue-800 hover:underline focus:outline-none"
                                      >
                                        See my reviews
                                      </button>
                                    )}
                                  </div>
                                  {reviewsLoading ? (
                                    <div className="text-sm text-gray-600 italic">Loading ratings...</div>
                                  ) : (
                                    <div className="space-y-2">
                                      {appointment.assignments?.map((assignment) => {
                                        const review = appointmentReviews[appointment.id]?.find(
                                          (r: ServiceReview) => r.serviceId === assignment.serviceId
                                        );
                                        
                                        return (
                                          <div key={assignment.serviceId} className="flex items-center justify-between py-1">
                                            <span className="text-sm text-gray-900">
                                              {assignment.serviceName}
                                            </span>
                                            <div className="flex items-center gap-2">
                                              {review ? (
                                                <div className="flex items-center gap-1">
                                                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                                  <span className="text-sm text-gray-900 font-medium">({review.rating}/5)</span>
                                                </div>
                                              ) : (
                                                <span className="text-sm text-gray-500 italic">Not rated</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Action buttons at bottom of card */}
                              <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-5 border-t border-gray-200 relative">
                                {/* View Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewAppointment(appointment);
                                  }}
                                  className="aacore-btn-primary w-full sm:w-auto justify-center"
                                >
                                  <Eye className="w-4 h-4" />
                                  View
                                </button>
                                
                                {/* Manage Dropdown */}
                                {(() => {
                                  const isPast = isAppointmentInPast(appointment);
                                  if (isPast) return null;

                                  const canReschedule = appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
                                  const canCancel = appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
                                  const showDeposit = isPendingDepositAppointment(appointment);
                                  const hasActions = canReschedule || canCancel || showDeposit;

                                  if (!hasActions) return null;

                                  return (
                                    <div className="relative w-full sm:w-auto" ref={el => manageDropdownRefs.current[appointment.id] = el}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleManageDropdown(appointment.id);
                                        }}
                                        className="aacore-btn-outlined w-full sm:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative z-10"
                                      >
                                        <Settings className="w-4 h-4" />
                                        Manage <ChevronDown className={`w-4 h-4 transition-transform ${openManageDropdowns.has(appointment.id) ? 'rotate-180' : ''}`} />
                                      </button>
                                      {openManageDropdowns.has(appointment.id) && (
                                        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-hidden">
                                          {showDeposit && (
                                            <Link
                                              to={`/dashboard/customer/appointments/${appointment.id}/deposit`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleManageDropdown(appointment.id);
                                              }}
                                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                                            >
                                              <DollarSign className="w-4 h-4 mr-2" />
                                              Deposit
                                            </Link>
                                          )}
                                          {canReschedule && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleManageDropdown(appointment.id);
                                                handleRescheduleAppointment(appointment);
                                              }}
                                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                                            >
                                              <Edit className="w-4 h-4 mr-2" />
                                              Reschedule Appointment
                                            </button>
                                          )}
                                          {canCancel && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleManageDropdown(appointment.id);
                                                handleCancelAppointment(appointment);
                                              }}
                                              className={`w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer ${canReschedule ? '' : ''}`}
                                            >
                                              <Trash2 className="w-4 h-4 mr-2" />
                                              Cancel Appointment
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {(() => {
                                  const canRebook =
                                    appointment.status === 'COMPLETED' ||
                                    (isAppointmentInPast(appointment) && appointment.status !== 'CANCELLED');
                                  if (!canRebook) return null;
                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRebookAppointment(appointment);
                                      }}
                                      className="aacore-btn2-primary rounded-full flex-1 sm:flex-none"
                                    >
                                      <CalendarPlus className="w-4 h-4" />
                                      Rebook
                                    </button>
                                  );
                                })()}
                                
                                {(() => {
                                  // Only show button if appointment is in past, not cancelled, and has no reviews/ratings
                                  const hasReviewsOrRatings = hasAnyReviewsOrRatings(appointment);
                                  const canLeaveReview =
                                    isAppointmentInPast(appointment) &&
                                    appointment.status !== 'CANCELLED' &&
                                    !hasReviewsOrRatings;

                                  if (!canLeaveReview) return null;

                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRateAppointment(appointment);
                                      }}
                                      className="aacore-btn2-success rounded-full flex-1 sm:flex-none"
                                    >
                                      <Star className="w-4 h-4" />
                                      Leave Reviews
                                    </button>
                                  );
                                })()}

                                {!isAppointmentInPast(appointment) &&
                                  appointment.status !== 'CANCELLED' &&
                                  appointment.status !== 'COMPLETED' && (
                                  <>
                                    {/* Add to Calendar Dropdown */}
                                    <div className="relative w-full sm:w-auto" ref={el => calendarDropdownRefs.current[appointment.id] = el}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleCalendarDropdown(appointment.id);
                                        }}
                                        className="aacore-btn-outlined w-full sm:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      >
                                        <CalendarPlus className="w-4 h-4" />
                                        Add to Calendar
                                        <ChevronDown className={`w-4 h-4 transition-transform ${openCalendarDropdowns.has(appointment.id) ? 'rotate-180' : ''}`} />
                                      </button>
                                      {openCalendarDropdowns.has(appointment.id) && (
                                        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-hidden">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              toggleCalendarDropdown(appointment.id);
                                              window.open(getGoogleCalendarUrl(appointment), '_blank');
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                                          >
                                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                            </svg>
                                            Google Calendar
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              toggleCalendarDropdown(appointment.id);
                                              window.open(getOutlookCalendarUrl(appointment), '_blank');
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer"
                                          >
                                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                              <path d="M7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V9H6v10.5A1.5 1.5 0 0 0 7.5 21zM6 7.5V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V7.5H6z" fill="#0078D4"/>
                                              <path d="M12 12.75c-1.24 0-2.25-1.01-2.25-2.25S10.76 8.25 12 8.25s2.25 1.01 2.25 2.25S13.24 12.75 12 12.75z" fill="#0078D4"/>
                                            </svg>
                                            Outlook
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                                
                              </div>
                              </div>
                              
                              {/* Show More/Less Button - Bottom Right */}
                              <div className="flex justify-end mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAppointmentCard(appointment.id);
                                  }}
                                  className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                >
                                  <span>{isExpanded ? 'Show Less' : 'Show More'}</span>
                                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-8">
                  <button
                    onClick={() => {
                      const hasFilters = selectedBusinessId || selectedProfessionalId || searchQuery.trim();
                      if (hasFilters) {
                        setFilteredPage(prev => Math.max(1, prev - 1));
                      } else {
                        handlePageChange(pagination.currentPage - 1);
                      }
                    }}
                    disabled={!pagination.hasPrevPage}
                    className={pagination.hasPrevPage ? 'aacore-btn-outlined' : 'aacore-btn-outlined'}
                  >
                    Previous
                  </button>
                  
                    <div className="flex items-center gap-1">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                      const hasFilters = selectedBusinessId || selectedProfessionalId || searchQuery.trim();
                      return (
                      <button
                        key={page}
                        onClick={() => {
                          if (hasFilters) {
                            setFilteredPage(page);
                          } else {
                            handlePageChange(page);
                          }
                        }}
                          className={page === pagination.currentPage ? 'aacore-btn-primary' : 'aacore-btn-outlined'}
                      >
                        {page}
                      </button>
                    )})}
                  </div>
                  
                  <button
                    onClick={() => {
                      const hasFilters = selectedBusinessId || selectedProfessionalId || searchQuery.trim();
                      if (hasFilters) {
                        setFilteredPage(prev => prev + 1);
                      } else {
                        handlePageChange(pagination.currentPage + 1);
                      }
                    }}
                    disabled={!pagination.hasNextPage}
                    className={pagination.hasNextPage ? 'aacore-btn-outlined' : 'aacore-btn-outlined'}
                  >
                    Next
                  </button>
                </div>
              )}
              
              {/* Pagination Info */}
              {pagination.total > 0 && (
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">
                    Showing {((pagination.currentPage - 1) * itemsPerPage) + 1} to {Math.min(pagination.currentPage * itemsPerPage, pagination.total)} of {pagination.total} appointments
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      );

      case 'quote-requests': {
        const formatQuoteRequestNumber = (id: string) => (id ? `Q-${id.slice(0, 8)}` : '—');
        const formatQuoteSubmitted = (createdAt: string) => {
          if (!createdAt) return '—';
          const d = new Date(createdAt);
          if (Number.isNaN(d.getTime())) return '—';
          return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        };
        const getQuoteServiceNames = (quote: QuoteRow) =>
          (quote.services || []).map((s) => (s.name || '').trim()).filter(Boolean).join(', ') || '—';

        const quoteStatusOptions = [
          'Request Submitted',
          'Offer Received',
          'Offer Expired',
          'Offer Accepted',
          'Pending Deposit',
          'Appointment Created',
          'Appointment Cancelled',
          'Deposit Received',
        ];

        const searchTrim = customerQuoteSearch.trim().toLowerCase();
        const filteredCustomerQuotes = customerQuotes
          .filter((quote) => {
            const displayStatus = getCustomerQuoteDisplayStatus(quote);
            if (customerQuoteStatusFilter && displayStatus.label !== customerQuoteStatusFilter) {
              return false;
            }
            if (!searchTrim) return true;
            const haystack = [
              quote.id,
              formatQuoteRequestNumber(quote.id),
              quote.businessName,
              quote.businessShortName,
              getQuoteServiceNames(quote),
              displayStatus.label,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return haystack.includes(searchTrim);
          })
          .slice()
          .sort((a, b) => {
            const aTime = new Date(a.createdAt || 0).getTime();
            const bTime = new Date(b.createdAt || 0).getTime();
            return customerQuoteSortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
          });

        return (
          <div className="space-y-4">
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#0a3d66')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#0B4778')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>
            <div className="mb-4">
              <h1 className="text-xl font-bold mb-0">My Quote Requests</h1>
              <p className="text-gray-600 text-sm mb-0">View and track your service quote requests</p>
            </div>
            {customerQuotesLoading ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-900">Loading quote requests...</p>
                </div>
              </div>
            ) : customerQuotes.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No quote requests</h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    You haven&apos;t requested any quotes yet. Request a quote from a business to see them here.
                  </p>
                  <Link to="/search" className="mt-4 inline-block aacore-btn-primary">
                    Find a service
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-white border border-gray-200 rounded-2xl p-3">
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
                    <div className="relative w-full sm:min-w-[12rem] sm:flex-1 sm:basis-[16rem] sm:max-w-md">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={customerQuoteSearch}
                        onChange={(e) => setCustomerQuoteSearch(e.target.value)}
                        className={`aacore-textfield-primary pl-10 ${customerQuoteSearch ? 'pr-10' : ''}`}
                        placeholder="Search request #, business, service..."
                        aria-label="Search quote requests"
                      />
                      {customerQuoteSearch && (
                        <button
                          type="button"
                          onClick={() => setCustomerQuoteSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                          aria-label="Clear search"
                        >
                          <span className="text-lg leading-none">&times;</span>
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:flex-none">
                      <div className="relative min-w-0 sm:min-w-[10rem]">
                        <select
                          value={customerQuoteStatusFilter}
                          onChange={(e) => setCustomerQuoteStatusFilter(e.target.value)}
                          className="aacore-textfield-primary appearance-none pr-10 w-full"
                          aria-label="Filter by status"
                        >
                          <option value="">All statuses</option>
                          {quoteStatusOptions.map((label) => (
                            <option key={label} value={label}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>

                      <div className="relative min-w-0 sm:min-w-[9.5rem]">
                        <select
                          value={customerQuoteSortOrder}
                          onChange={(e) => setCustomerQuoteSortOrder(e.target.value as 'newest' | 'oldest')}
                          className="aacore-textfield-primary appearance-none pr-10 w-full"
                          aria-label="Sort order"
                        >
                          <option value="newest">Newest first</option>
                          <option value="oldest">Oldest first</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>

                {filteredCustomerQuotes.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-12 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                        <Search className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No matching quote requests</h3>
                      <p className="text-sm text-gray-600 max-w-md mx-auto">
                        Try adjusting your search or status filter.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCustomerQuotes.map((quote) => {
                      const displayStatus = getCustomerQuoteDisplayStatus(quote);
                      return (
                        <div
                          key={quote.id}
                          className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-visible"
                        >
                          <div className="px-6 pt-6 pb-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <span className="text-sm font-mono text-gray-500 uppercase">{formatQuoteRequestNumber(quote.id)}</span>
                                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${displayStatus.badgeClass}`}>
                                    {displayStatus.label}
                                  </span>
                                </div>
                                <div className="text-lg font-bold text-gray-900 mb-1">
                                  {quote.businessShortName ? (
                                    <Link
                                      to={`/${quote.businessShortName}`}
                                      className="text-[#0B4778] hover:underline"
                                    >
                                      {quote.businessName || 'Business'}
                                    </Link>
                                  ) : (
                                    (quote.businessName || 'Business')
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mb-1">
                                  {getQuoteServiceNames(quote)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Submitted {formatQuoteSubmitted(quote.createdAt)}
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <Link
                                  to={`/dashboard/customer/quote-requests/${quote.id}`}
                                  className="aacore-btn-outlined inline-flex items-center gap-2"
                                >
                                  <Eye className="w-4 h-4" />
                                  View
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        );
      }
        
      case 'invoices':
        return (
          <div className="space-y-4">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#0a3d66')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#0B4778')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>

            <CustomerInvoicesSection />
          </div>
        );
        
      case 'gift-cards':
        // Helper function to get gift card status icon and color
        const getGiftCardStatusIcon = (status: string) => {
          switch (status) {
            case 'ACTIVE':
              return { icon: CheckCircle, color: 'bg-green-100 text-green-600' };
            case 'REDEEMED':
              return { icon: CheckCircle, color: 'bg-blue-50 text-blue-900' };
            case 'EXPIRED':
              return { icon: XCircle, color: 'bg-orange-100 text-orange-600' };
            case 'CANCELLED':
              return { icon: XCircle, color: 'bg-red-100 text-red-600' };
            default:
              return { icon: Gift, color: 'bg-gray-100 text-gray-600' };
          }
        };

        const getGiftCardStatusColor = (status: string) => {
          switch (status) {
            case 'ACTIVE':
              return 'bg-green-100 text-green-800';
            case 'REDEEMED':
              return 'bg-blue-50 text-blue-900';
            case 'EXPIRED':
              return 'bg-orange-100 text-orange-800';
            case 'CANCELLED':
              return 'bg-red-100 text-red-800';
            default:
              return 'bg-gray-100 text-gray-800';
          }
        };

        const totalBalance = giftCards.reduce((sum, gc) => sum + (gc.balance || 0), 0);
        const displayAmount = (() => {
          if (customAmount.trim()) {
            const parsed = parseFloat(customAmount);
            return Number.isFinite(parsed) ? parsed : 0;
          }
          return selectedAmount;
        })();
        const canProceedToPayment =
          displayAmount >= 10 &&
          recipientName.trim().length > 0 &&
          recipientEmail.trim().length > 0 &&
          (purchaseTab !== 'store' || !!selectedGiftCardBusinessId);

        return (
          <div className="space-y-4">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>

            {/* Page Header */}
            <div className="mb-4">
              <h1 className="text-xl font-bold mb-0">Gift Cards</h1>
              <p className="text-gray-600 text-sm mb-0">Buy, send, and manage balances for any AACore Booking store.</p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: My Gift Cards */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-base font-semibold mb-0">My Gift Cards</h2>
                    <span className="badge bg-gray-100 text-gray-800 border border-gray-200 text-xs px-2 py-1 rounded-full">
                      <Wallet2 className="w-3 h-3 inline mr-1" />
                      Total Balance: <strong>${totalBalance.toFixed(2)}</strong>
                    </span>
                  </div>

                  {giftCardsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                      <p className="text-sm text-gray-600">Loading your gift cards...</p>
                    </div>
                  ) : giftCardsError ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-red-600">{giftCardsError}</p>
                    </div>
                  ) : giftCards.length > 0 ? (
                    <div className="space-y-3">
                      {giftCards.map((giftCard) => {
                        const isUniversal = !giftCard.businessId;
                        const { icon: StatusIcon, color: statusColor } = getGiftCardStatusIcon(giftCard.status);
                        return (
                          <div key={giftCard.id} className="p-3 rounded-lg border border-gray-200 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className={`${isUniversal ? 'bg-blue-100 text-blue-600' : 'bg-yellow-100 text-yellow-600'} rounded-lg p-2 flex-shrink-0`}>
                                {isUniversal ? <Globe className="w-5 h-5" /> : <Store className="w-5 h-5" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-gray-900">
                                  {isUniversal ? 'AACore Universal Gift Card' : `${giftCard.business?.name || 'Store'} Gift Card`}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {isUniversal ? 'Use at any business that accepts AACore Booking' : `Valid only at ${giftCard.business?.name || 'this store'}`}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${getGiftCardStatusColor(giftCard.status)}`}>
                                    {giftCard.status}
                                  </span>
                                  <span className="text-xs text-gray-500">Card ending ···· {giftCard.code.slice(-4)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="font-bold text-lg">${giftCard.balance.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">
                                Updated {new Date(giftCard.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                              <div className="flex gap-2 justify-end mt-2">
                                <button
                                  onClick={() => {
                                    setSelectedGiftCardForDetails(giftCard);
                                    setIsDetailsModalOpen(true);
                                  }}
                                  className="aacore-btn-outlined"
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Details
                                </button>
                                <button className="aacore-btn-primary">
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Use
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center border rounded-lg p-6 bg-gray-50">
                      <Gift className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <div className="font-semibold text-sm mb-1">No gift cards yet</div>
                      <div className="text-xs text-gray-600">Buy one below or redeem a code.</div>
                    </div>
                  )}

                  {/* Redeem Code Section */}
                  <hr className="my-4" />
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleRedeemCode();
                    }}
                    className="space-y-2"
                  >
                    <label htmlFor="redeemCode" className="block text-xs font-semibold text-gray-900">
                      Redeem a Gift Card
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        id="redeemCode"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value)}
                        placeholder="Enter code (e.g., GC-AB12-34CD)"
                        className="flex-1 aacore-textfield-primary"
                        disabled={isRedeeming}
                      />
                      <button
                        type="submit"
                        className="aacore-btn-outlined whitespace-nowrap"
                        disabled={isRedeeming || !redeemCode.trim()}
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        {isRedeeming ? 'Redeeming...' : 'Redeem'}
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      Codes from a store website or email will link to your account and appear above.
                    </div>
                  </form>
                </div>
              </div>

              {/* Right: Buy Gift Cards */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm" id="buy">
                <div className="p-5">
                  <div className="flex justify-between items-start flex-wrap gap-2 mb-4">
                    <h2 className="text-base font-semibold mb-0">Buy Gift Card</h2>
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <Lock className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <div className="flex flex-col">
                        <div className="text-xs font-semibold text-green-900 leading-tight">Secure Checkout</div>
                        <div className="text-xs text-green-700 leading-tight">powered by AACore</div>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex gap-2 mb-4 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setPurchaseTab('store');
                        setSelectedGiftCardBusinessId('');
                        setSelectedGiftCardBusinessName('');
                      }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        purchaseTab === 'store'
                          ? 'border-blue-900 text-blue-900'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      For a Specific Store
                    </button>
                    <button
                      type="button"
                      onClick={() => setPurchaseTab('universal')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        purchaseTab === 'universal'
                          ? 'border-blue-900 text-blue-900'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Use Anywhere on AACore
                    </button>
                  </div>

                  {/* Store-Specific Tab */}
                  {purchaseTab === 'store' && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handlePurchaseGiftCard();
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="storeSearch" className="block text-xs font-semibold text-gray-900 mb-1">
                          Choose a Store
                        </label>
                        <input
                          type="search"
                          id="storeSearch"
                          value={storeSearchQuery}
                          onChange={(e) => setStoreSearchQuery(e.target.value)}
                          placeholder="Search by name or city (e.g., Sunflower Nails, Reston)"
                          className="aacore-textfield-primary mb-2"
                        />
                        <div className="text-xs text-gray-500 mb-2">Recently visited</div>
                        <div className="flex flex-wrap gap-2">
                          {recentBusinesses.slice(0, 2).map((business) => (
                            <button
                              key={business.id}
                              type="button"
                              onClick={() => {
                                setSelectedGiftCardBusinessId(business.id);
                                setSelectedGiftCardBusinessName(business.name);
                              }}
                              className={`px-3 py-2 text-xs border rounded-lg transition-colors ${
                                selectedGiftCardBusinessId === business.id
                                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <Store className="w-3 h-3 inline mr-1" />
                              {business.name} {business.city ? `· ${business.city}` : ''}
                            </button>
                          ))}
                        </div>
                        {searchedBusinesses.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {searchedBusinesses.slice(0, 5).map((business) => (
                              <button
                                key={business.id}
                                type="button"
                                onClick={() => {
                                  setSelectedGiftCardBusinessId(business.id);
                                  setSelectedGiftCardBusinessName(business.name);
                                }}
                                className={`w-full text-left px-3 py-2 text-xs border rounded-lg transition-colors ${
                                  selectedGiftCardBusinessId === business.id
                                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                              >
                                {business.name} {business.city ? `· ${business.city}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-900 mb-2">Amount</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {[25, 50, 75, 100, 150].map((amt) => (
                            <button
                              key={amt}
                              type="button"
                              onClick={() => {
                                setSelectedAmount(amt);
                                setCustomAmount('');
                              }}
                              className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${
                                selectedAmount === amt && !customAmount
                                  ? 'border-blue-500 bg-blue-50 text-blue-900 font-semibold'
                                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                              }`}
                            >
                              ${amt}
                            </button>
                          ))}
                        </div>
                        <div className="mt-3">
                          <div className="relative flex items-center">
                            <div className="absolute left-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 font-medium">$</span>
                            </div>
                            <input
                              type="number"
                              min="10"
                              step="1"
                              value={customAmount}
                              onChange={(e) => {
                                setCustomAmount(e.target.value);
                                if (e.target.value) setSelectedAmount(0);
                              }}
                              placeholder="Enter custom amount"
                              className="aacore-textfield-primary pl-8 w-full"
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1.5">Minimum $10. Pick a preset above or enter a custom amount.</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="recipientName1" className="block text-xs font-semibold text-gray-900 mb-1">
                            Recipient Name
                          </label>
                          <input
                            type="text"
                            id="recipientName1"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            placeholder="Full name"
                            className="aacore-textfield-primary"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="recipientEmail1" className="block text-xs font-semibold text-gray-900 mb-1">
                            Recipient Email
                          </label>
                          <input
                            type="email"
                            id="recipientEmail1"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            placeholder="name@example.com"
                            className="aacore-textfield-primary"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message1" className="block text-xs font-semibold text-gray-900 mb-1">
                          Message (optional)
                        </label>
                        <textarea
                          id="message1"
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          rows={2}
                          placeholder="Add a short note"
                          className="aacore-textfield-primary"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="deliveryDate1" className="block text-xs font-semibold text-gray-900 mb-1">
                            Delivery
                          </label>
                          <input
                            type="date"
                            id="deliveryDate1"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            className="aacore-textfield-primary"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            We'll email the gift card to the recipient on this date.
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-900 mb-1">Summary</label>
                          <div className="p-2 border rounded-lg text-xs bg-gray-50">
                            <div className="flex justify-between mb-1">
                              <span>Card Type</span>
                              <span>{selectedGiftCardBusinessName || 'Store'}</span>
                            </div>
                            <div className="flex justify-between mb-1">
                              <span>Amount</span>
                              <span>${displayAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                              <span>Total</span>
                              <span>${displayAmount.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="aacore-btn-primary w-full justify-center gap-2"
                        disabled={isPurchasing || !canProceedToPayment || !giftPurchaseStripePromise}
                      >
                        {isPurchasing ? 'Starting checkout…' : 'Proceed to Payment'}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </form>
                  )}

                  {/* Universal Tab */}
                  {purchaseTab === 'universal' && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handlePurchaseGiftCard();
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-xs font-semibold text-gray-900 mb-2">Amount</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {[25, 50, 100, 200].map((amt) => (
                            <button
                              key={amt}
                              type="button"
                              onClick={() => {
                                setSelectedAmount(amt);
                                setCustomAmount('');
                              }}
                              className={`px-3 py-1.5 text-sm border rounded-full transition-colors ${
                                selectedAmount === amt && !customAmount
                                  ? 'border-blue-500 bg-blue-50 text-blue-900 font-semibold'
                                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                              }`}
                            >
                              ${amt}
                            </button>
                          ))}
                        </div>
                        <div className="mt-3">
                          <div className="relative flex items-center">
                            <div className="absolute left-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 font-medium">$</span>
                            </div>
                            <input
                              type="number"
                              min="10"
                              step="1"
                              value={customAmount}
                              onChange={(e) => {
                                setCustomAmount(e.target.value);
                                if (e.target.value) setSelectedAmount(0);
                              }}
                              placeholder="Enter custom amount"
                              className="aacore-textfield-primary pl-8 w-full"
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1.5">Minimum $10. Use anywhere AACore Booking is accepted.</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="recipientName2" className="block text-xs font-semibold text-gray-900 mb-1">
                            Recipient Name
                          </label>
                          <input
                            type="text"
                            id="recipientName2"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            placeholder="Full name"
                            className="aacore-textfield-primary"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="recipientEmail2" className="block text-xs font-semibold text-gray-900 mb-1">
                            Recipient Email
                          </label>
                          <input
                            type="email"
                            id="recipientEmail2"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            placeholder="name@example.com"
                            className="aacore-textfield-primary"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message2" className="block text-xs font-semibold text-gray-900 mb-1">
                          Message (optional)
                        </label>
                        <textarea
                          id="message2"
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          rows={2}
                          placeholder="Add a short note"
                          className="aacore-textfield-primary"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="deliveryDate2" className="block text-xs font-semibold text-gray-900 mb-1">
                            Delivery
                          </label>
                          <input
                            type="date"
                            id="deliveryDate2"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            className="aacore-textfield-primary"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            We'll email the gift card to the recipient on this date.
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-900 mb-1">Summary</label>
                          <div className="p-2 border rounded-lg text-xs bg-gray-50">
                            <div className="flex justify-between mb-1">
                              <span>Card Type</span>
                              <span>Universal</span>
                            </div>
                            <div className="flex justify-between mb-1">
                              <span>Amount</span>
                              <span>${displayAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                              <span>Total</span>
                              <span>${displayAmount.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="aacore-btn-primary w-full justify-center gap-2"
                        disabled={isPurchasing || !canProceedToPayment || !giftPurchaseStripePromise}
                      >
                        {isPurchasing ? 'Starting checkout…' : 'Proceed to Payment'}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </form>
                  )}

                  <hr className="my-4" />
                  <div className="text-xs text-gray-500">
                    Gift cards purchased on a store's website automatically appear here after checkout (linked to your account email).
                    Universal gift cards work across all AACore Booking stores that enable gift redemption.
                  </div>
                </div>
              </div>
            </div>

            <CustomerGiftCardPurchaseModal
              isOpen={isGiftPurchaseModalOpen}
              onClose={() => {
                setIsGiftPurchaseModalOpen(false);
                setGiftPurchaseClientSecret(null);
                setGiftPurchaseContext(null);
              }}
              onPurchased={handleGiftCardPurchaseComplete}
              stripePromise={giftPurchaseStripePromise}
              clientSecret={giftPurchaseClientSecret}
              context={giftPurchaseContext}
              amountLabel={displayAmount.toFixed(2)}
            />

            {/* Gift Card Details Modal */}
            {isDetailsModalOpen && selectedGiftCardForDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold">
                      {selectedGiftCardForDetails.business ? `${selectedGiftCardForDetails.business.name} Gift Card` : 'Universal Gift Card'} · ···· {selectedGiftCardForDetails.code.slice(-4)}
                    </h3>
                    <button
                      onClick={() => {
                        setIsDetailsModalOpen(false);
                        setSelectedGiftCardForDetails(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <div className="text-xs text-gray-500">Current Balance</div>
                        <div className="text-2xl font-bold">${selectedGiftCardForDetails.balance.toFixed(2)}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-xs text-gray-500">Card Code</div>
                        <div className="bg-gray-900 text-blue-100 px-3 py-1 rounded-lg font-mono text-sm">
                          {selectedGiftCardForDetails.code}
                        </div>
                      </div>
                    </div>
                    
                    {/* QR Code Section */}
                    <div className="flex justify-center mb-4">
                      <div className="bg-white p-4 border border-gray-200 rounded-lg">
                        <QRCodeSVG
                          value={selectedGiftCardForDetails.code}
                          size={150}
                          level="H"
                          includeMargin={true}
                        />
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <div className="text-xs text-gray-500">Scan QR code to redeem</div>
                    </div>
                    
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <div className="text-xs font-semibold mb-2">Recent Activity</div>
                      <ul className="space-y-1 text-xs">
                        <li className="flex justify-between">
                          <span>Loaded (purchase)</span>
                          <span className="text-green-600">+ ${selectedGiftCardForDetails.amount.toFixed(2)}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-6 border-t border-gray-200 flex gap-2">
                    <button className="aacore-btn-outlined flex-1">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Funds
                    </button>
                    <button className="aacore-btn-primary flex-1">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      {selectedGiftCardForDetails.business ? `Use at ${selectedGiftCardForDetails.business.name}` : 'Use Now'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        

        
      case 'notifications':
        // Helper function to get notification icon and color
        const getNotificationIcon = (type: string) => {
          switch (type) {
            case 'APPOINTMENT_BOOKED':
              return { icon: CheckCircle, color: 'text-[#0B4778]' };
            case 'APPOINTMENT_CANCELLED':
              return { icon: XCircle, color: 'text-red-600' };
            case 'APPOINTMENT_RESCHEDULED':
              return { icon: CalendarClock, color: 'text-[#0B4778]' };
            case 'APPOINTMENT_DEPOSIT_REQUESTED':
              return { icon: DollarSign, color: 'text-amber-600' };
            case 'TESTIMONIAL_SUBMITTED':
              return { icon: Star, color: 'text-green-600' };
            case 'GIFT_CARD_REDEEMED':
              return { icon: Gift, color: 'text-teal-600' };
            case 'SYSTEM_ANNOUNCEMENT':
              return { icon: AlertCircle, color: 'text-pink-600' };
            case 'QUOTE_BUSINESS_MESSAGE':
              return { icon: MessageSquare, color: 'text-[#0B4778]' };
            case 'QUOTE_REQUEST_SUBMITTED':
              return { icon: FileText, color: 'text-[#0B4778]' };
            case 'QUOTE_OFFER_RECEIVED':
              return { icon: FileText, color: 'text-[#0B4778]' };
            case 'QUOTE_OFFER_ACCEPTED':
              return { icon: CheckCircle, color: 'text-green-600' };
            case 'INVOICE_SENT':
              return { icon: FileText, color: 'text-amber-600' };
            case 'INVOICE_PAID':
              return { icon: CheckCircle, color: 'text-emerald-600' };
            default:
              return { icon: Bell, color: 'text-amber-500' };
          }
        };

        // Helper function to format time
        const formatNotificationTime = (dateString: string) => {
          const date = new Date(dateString);
          const now = new Date();
          const diffMs = now.getTime() - date.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          if (diffMins < 1) return 'Just now';
          if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
          if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
          if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
        };

        return (
          <div className="space-y-4">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/customer')}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
            </div>

            {/* Page Header */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold mb-0">Notifications</h1>
                <p className="text-gray-600 text-sm mb-0">Stay updated with your appointments and account activity</p>
              </div>
              {unreadNotifications.length > 0 && (
                <button
                  onClick={handleMarkAllNotificationsAsRead}
                  className="hidden md:block aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4778]"
                >
                  <i className="bi bi-check-all mr-2"></i>
                  Mark All as Read
                </button>
              )}
            </div>

            {/* Mobile Mark All as Read Button - Full Width */}
            {unreadNotifications.length > 0 && (
              <div className="mb-4 md:hidden">
                <button
                  onClick={handleMarkAllNotificationsAsRead}
                  className="w-full flex items-center justify-center aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4778]"
                >
                  <i className="bi bi-check-all mr-2"></i>
                  Mark All as Read
                </button>
              </div>
            )}
            
            {notificationsLoading ? (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-900">Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length > 0 ? (
              <div className="space-y-8">
                {/* Unread Notifications */}
                {unreadNotifications.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px flex-1 bg-gray-200"></div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <h2 className="text-lg font-semibold text-gray-900">
                      Unread ({unreadNotifications.length})
                        </h2>
                              </div>
                      <div className="h-px flex-1 bg-gray-200"></div>
                            </div>
                    <div className="space-y-2.5">
                      {unreadNotifications.map((notification) => {
                        const { icon: Icon, color } = getNotificationIcon(notification.type);
                        const isUnread = true;
                        
                        // Build meta information
                        const metaParts: (string | JSX.Element)[] = [];
                        metaParts.push(formatNotificationTime(notification.created_at));
                        if (notification.business_id && notification.business_id !== 'system') {
                          metaParts.push(`Business: ${businessNames[notification.business_id] || notification.business_id}`);
                        }
                        
                        return (
                          <div 
                            key={notification.id} 
                            className={`grid grid-cols-[auto_1fr_auto] gap-3 items-center border border-gray-200 rounded-xl p-3 bg-white shadow-sm ${
                              isUnread ? 'border-l-4 border-l-green-500' : ''
                            }`}
                          >
                            {/* Badge Icon */}
                            <div className={`w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center ${color}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            
                            {/* Summary */}
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 text-sm leading-tight mb-1 flex items-center gap-2">
                                <span>{notification.title}</span>
                              </div>
                              <div className="text-xs text-gray-500 mb-1">
                                {notification.message}
                              </div>
                              <div className="text-xs text-gray-500">
                                {metaParts.map((part, index) => (
                                  <span key={index}>
                                    {index > 0 && ' • '}
                                    {part}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleMarkNotificationAsRead(notification.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0B4778]/10 text-[#0B4778] border border-[#0B4778]/30 hover:bg-[#0B4778]/20 transition-colors"
                              >
                                <Check className="w-4 h-4" />
                                Mark read
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Read Notifications */}
                {readNotifications.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px flex-1 bg-gray-200"></div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <h2 className="text-lg font-semibold text-gray-900">
                      Read ({readNotifications.length})
                        </h2>
                      </div>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>
                    <div className="space-y-2.5">
                      {readNotifications.map((notification) => {
                        const { icon: Icon, color } = getNotificationIcon(notification.type);
                        const isUnread = false;
                        
                        // Build meta information
                        const metaParts: (string | JSX.Element)[] = [];
                        metaParts.push(formatNotificationTime(notification.created_at));
                        if (notification.read_at) {
                          metaParts.push(`Read ${formatNotificationTime(notification.read_at)}`);
                        }
                        if (notification.business_id && notification.business_id !== 'system') {
                          metaParts.push(`Business: ${businessNames[notification.business_id] || notification.business_id}`);
                        }
                        
                        return (
                          <div 
                            key={notification.id} 
                            className={`grid grid-cols-[auto_1fr_auto] gap-3 items-center border border-gray-200 rounded-xl p-3 bg-white shadow-sm ${
                              isUnread ? 'border-l-4 border-l-green-500' : ''
                            }`}
                          >
                            {/* Badge Icon */}
                            <div className={`w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center ${color}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            
                            {/* Summary */}
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 text-sm leading-tight mb-1 flex items-center gap-2">
                                <span>{notification.title}</span>
                              </div>
                              <div className="text-xs text-gray-500 mb-1">
                                {notification.message}
                              </div>
                              <div className="text-xs text-gray-500">
                                {metaParts.map((part, index) => (
                                  <span key={index}>
                                    {index > 0 && ' • '}
                                    {part}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <Bell className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications</h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    You'll see notifications here when you have updates about your appointments, reviews, and account activity.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  // Guard against unauthenticated users
  if (!user) return null;

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => handleSectionChange('notifications')}
      />
      
      {/* Main Content */}
      <div className="flex-1 min-w-0 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto">
          {renderSectionContent()}
        </div>
      </div>

      {/* Service Review Modal */}
      {selectedAppointment && (
        <ServiceReviewModal
          appointment={selectedAppointment}
          isOpen={isReviewModalOpen}
          onClose={handleReviewModalClose}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}

             {/* Customer Cancellation Modal */}
       {cancellationAppointment && (
         <CustomerCancellationModal
           isOpen={isCancellationModalOpen}
           onClose={handleCancellationCancelled}
           onConfirm={handleCancellationConfirmed}
           appointmentDetails={(() => {
             const iso = getAppointmentStartIso(cancellationAppointment);
             const when = iso ? `${new Date(iso).toLocaleDateString()} at ${new Date(iso).toLocaleTimeString()}` : 'Date TBD';
             return `${cancellationAppointment.assignments?.map(assignment => assignment.serviceName).join(', ') || 'Appointment'} at ${cancellationAppointment.business_name} on ${when}`;
           })()}
           appointment={cancellationAppointment as any}
           isLoading={isCancelling}
         />
       )}

      <RebookConfirmModal
        isOpen={isRebookModalOpen}
        appointment={rebookAppointment}
        onClose={() => {
          setIsRebookModalOpen(false);
          setRebookAppointment(null);
        }}
        onEditServices={handleRebookEditServices}
        onContinue={handleRebookContinue}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CustomerDashboard; 